import type { ResolvedChartSegmentDefault } from '../chart-segment-default';
export type { ChartSegmentDefault, RecordedChartGroup, ResolvedChartSegmentDefault } from '../chart-segment-default';
// C3.3. Imported, not merely re-exported: `ProfilingCut` below types its role
// against the PLAN vocabulary, so the name has to be in this module's scope.
import type { PlanVerbatimContent } from "./plan";
import type { PlanProfilingRole } from "./plan";
import type { ReportCardShape } from "./report-card-capabilities";
import type { AdPlacement, DurationUnit, MessageClaimType } from "./vocabularies";
import type { StudySpec } from "./study-spec.generated";
import type { VisibilityRuleOperator } from "./condition-operators";
import {
  CanonicalAnalysisOutput,
  CanonicalSlideType as GeneratedCanonicalSlideType,
  MetricKind as GeneratedMetricKind,
  MetricResult as GeneratedMetricResult,
  PairedLiftNotApplicableBefore,
} from "../reporting";
import { QualitativeSlideType } from "./canonical-vocabularies";
// A hand reader type whose generated twin is the same type is re-exported
// from the generated projection; every other hand twin is
// pinned in `./canonical-analysis-output-parity.ts`.
import type * as GeneratedAnalysis from "../reporting/canonical-analysis-output.generated";
import type {
  AdAsset,
  AssetMediaRef,
  BrandAsset,
  ConceptAsset,
  LandingPageAsset,
  MessageAsset,
  PackagingAsset,
  PainAsset,
  ProductAsset,
  ProductAssetPrice,
  TestedAssetBlock,
  TestedAssetBlockMetadata,
  TestedAssetSharedContext,
  TestedAssetSharedContextSourceSegment,
  TestedAssetValidationState,
} from "./draft.generated";
export type {
  AdAsset,
  AssetMediaRef,
  BrandAsset,
  ConceptAsset,
  LandingPageAsset,
  MessageAsset,
  PackagingAsset,
  PainAsset,
  ProductAsset,
  ProductAssetPrice,
  TestedAssetBlock,
  TestedAssetBlockMetadata,
  TestedAssetSharedContext,
  TestedAssetSharedContextSourceSegment,
  TestedAssetValidationState,
};
// The compiled survey's `_canonicalConfig`, generated beside the draft.
import type {
  CanonicalAssetContext,
  CanonicalAssetContextItem,
  CanonicalAssetImageContext,
  CanonicalAssetImageContextItem,
  CanonicalAssetImageSlice,
  CanonicalAssetVideoContext,
  CanonicalAssetVideoContextItem,
  CanonicalChoiceAssetRefEntry,
  CanonicalQuestionMapEntry,
  CanonicalSurveyConfig,
} from "./draft.generated";
export type {
  CanonicalAssetContext,
  CanonicalAssetContextItem,
  CanonicalAssetImageContext,
  CanonicalAssetImageContextItem,
  CanonicalAssetImageSlice,
  CanonicalAssetVideoContext,
  CanonicalAssetVideoContextItem,
  CanonicalChoiceAssetRefEntry,
  CanonicalQuestionMapEntry,
  CanonicalSurveyConfig,
};
import type {
  AssetMeasureMetricSource,
  CustomTopicReportSource,
  FixedMatrixRows,
  MatrixMeasureMetricSource,
  MatrixRowMetricSource,
  QuestionCustomReportDisposition,
  QuestionCustomSurveyDataOnlyDisposition,
  QuestionMetricSource,
  RecipeModuleReportSource,
  ReportDeclaredBindingProjectionRequirement,
  ReportingProjectionSpec,
  ReportSourcesProjectionRequirement,
  TestedAssetsMatrixRows,
} from "./draft.generated";
import type {
  AssetMentionRef,
  AssetQuestionBinding,
  ChoiceOption,
  ChoicesFromRef,
  ContinuousResponse,
  MatrixCategoricalResponse,
  MatrixRatingMeasure,
  MatrixRatingResponse,
  MatrixRow,
  MultiSelectResponse,
  OpenEndResponse,
  QuestionDefinition,
  QuestionLaneExclusion,
  QuestionPlanAttribution,
  QuestionStatedPlacement,
  QuestionRoutingRule,
  QuestionVisibilityRule,
  QuestionVisibilityRuleGroup,
  RankingResponse,
  RatingAnchors,
  RatingResponse,
  SingleSelectResponse,
  TestedAssetsOptionSource,
  BrandRosterOptionSource,
} from "./draft.generated";
export type {
  AssetMentionRef,
  AssetQuestionBinding,
  ChoiceOption,
  ChoicesFromRef,
  ContinuousResponse,
  MatrixCategoricalResponse,
  MatrixRatingMeasure,
  MatrixRatingResponse,
  MatrixRow,
  MultiSelectResponse,
  OpenEndResponse,
  QuestionDefinition,
  QuestionLaneExclusion,
  QuestionPlanAttribution,
  QuestionStatedPlacement,
  QuestionRoutingRule,
  QuestionVisibilityRule,
  QuestionVisibilityRuleGroup,
  RankingResponse,
  RatingAnchors,
  RatingResponse,
  SingleSelectResponse,
  TestedAssetsOptionSource,
  BrandRosterOptionSource,
};
/** Which point of a rating scale is the good outcome (Python `RatingPolarity`). */
export type RatingPolarity = NonNullable<RatingAnchors["polarity"]>;
import type {
  IntakePointLabelIntent,
} from "./draft-refinements";
export type {
  IntakePointLabelIntent,
};
import type {
  Assumption,
  AudienceFilterLabel,
  AudienceSelectionRef,
  CompilerHints,
  CompositeMetricComponent,
  DesignBrief,
  DesignRecommendation,
  DraftValidationState,
  IntakeTargetShape,
  MetricDefinition,
  MetricDisplaySpec,
  MetricExtractionSpec,
  MetricPlan,
  NormalizationSpec,
  OpenEndClassifierSpec,
  PlanBrandFacts,
  QualitativeAnalysisDefinition,
  QuestionSlideMapping,
  QuestionnaireDraft,
  QuestionnaireExposureBoundary,
  QuestionnaireExposureMark,
  QuestionnairePlanCoverageRecord,
  QuestionnaireReviewOptions,
  QuestionnaireValidationIssue,
  ReportAxis,
  ReportCardPlan,
  ReportComparisonTable,
  ReportKpiSlotPlan,
  ReportLiftBaseline,
  ReportOptionPlan,
  ReportPriceCurve,
  ReportQuadrantAxes,
  ReportSlotPlan,
  ReportSlotsPlan,
  ReportSourceBinding,
  ReportingPlan,
  ReportingProjectionBindings,
  RequiredDeckSlides,
  SlideDataRequirements,
  SlidePlan,
  SlidePlanItem,
  StudyEvidenceConstraint,
  StudyEvidenceContract,
  StudyEvidenceRequirement,
  StudyGoals,
  StudyVerdictCompositeDefinition,
  WinnerCompositeDefinition,
} from "./draft.generated";
export type {
  Assumption,
  AudienceFilterLabel,
  AudienceSelectionRef,
  CompilerHints,
  CompositeMetricComponent,
  DesignBrief,
  DesignRecommendation,
  DraftValidationState,
  IntakeTargetShape,
  MetricDefinition,
  MetricDisplaySpec,
  MetricExtractionSpec,
  MetricPlan,
  NormalizationSpec,
  OpenEndClassifierSpec,
  PlanBrandFacts,
  QualitativeAnalysisDefinition,
  QuestionSlideMapping,
  QuestionnaireDraft,
  QuestionnaireExposureBoundary,
  QuestionnaireExposureMark,
  QuestionnairePlanCoverageRecord,
  QuestionnaireReviewOptions,
  QuestionnaireValidationIssue,
  ReportAxis,
  ReportCardPlan,
  ReportComparisonTable,
  ReportKpiSlotPlan,
  ReportLiftBaseline,
  ReportOptionPlan,
  ReportPriceCurve,
  ReportQuadrantAxes,
  ReportSlotPlan,
  ReportSlotsPlan,
  ReportSourceBinding,
  ReportingPlan,
  ReportingProjectionBindings,
  RequiredDeckSlides,
  SlideDataRequirements,
  SlidePlan,
  SlidePlanItem,
  StudyEvidenceConstraint,
  StudyEvidenceContract,
  StudyEvidenceRequirement,
  StudyGoals,
  StudyVerdictCompositeDefinition,
  WinnerCompositeDefinition,
};

/**
 * One planned composite, discriminated on `kind` (C15a): a scored
 * `winner_composite`, or the `study_verdict` marker that plans the podium on
 * the study verdict. Derived from the generated metric plan, never restated.
 */
export type CompositeMetricDefinition = MetricPlan["compositeMetrics"][number];

export {
  CHAT_LANGUAGES,
  DEFAULT_CONVERSATION_LANGUAGE,
  capitalisesNouns,
  chatLanguageDirection,
  conversationLanguageOf,
  isConversationLanguage,
} from "./chat-languages";
export type { ConversationLanguage, MessageLanguage } from "./chat-languages";
export {
  SCALE_WORDING,
  SURVEY_LANGUAGES,
  continuousRangeHint,
  durationUnitLabel,
  fillRespondentWording,
  notApplicableText,
  optionPlaceholderLabel,
  otherMixedCopy,
  otherMixedTitles,
  respondentWordingFor,
  scaleFamilyLabels,
  scaleWordingFor,
} from "./scale-wording-packs";
export type {
  RespondentWording,
  ScaleWordingFamily,
  ScaleWordingLanguage,
} from "./scale-wording-packs";
export {
  respondentNotApplicableOption,
  respondentRatingOptions,
  respondentRatingPointLabels,
  respondentScaleOptions,
  respondentScalePoints,
} from "./scale-options";
export type {
  RatingPointLabelIssue,
  RespondentScaleOption,
  StoredRatingScale,
} from "./scale-options";
export { QUESTIONNAIRE_REGISTRY_VERSION } from "./registry-version";
export {
  QUESTIONNAIRE_CONTRACT_DIGEST,
  QUESTIONNAIRE_CONTRACT_DIGEST_HEADER,
} from "./contract-digest.generated";
export type { QuestionnaireRegistryVersion } from "./registry-version";
export {
  QUESTIONNAIRE_CLARIFICATION_ORIGINS,
  closeClarification,
  collectReceiptFitsCard,
  openClarification,
  openClarificationOf,
  turnAnswersClarification,
} from "./clarification";
export type {
  QuestionnaireAnswering,
  QuestionnaireClarification,
  QuestionnaireClarificationClosedBy,
  QuestionnaireClarificationOrigin,
  QuestionnaireClarificationPending,
  QuestionnaireMaterialWantedPending,
  QuestionnaireSetObjectivePending,
} from "./clarification";
export {
  QUESTIONNAIRE_CHAT_TURN_STAGES,
  QUESTIONNAIRE_CHAT_TURN_STATUSES,
  QUESTIONNAIRE_CHAT_TURN_TERMINAL_STATUSES,
  QUESTIONNAIRE_DRAFT_SOCKET,
  QUESTIONNAIRE_TURN_ATTACHMENT_KINDS,
  QUESTIONNAIRE_TURN_ATTACHMENT_LIMIT,
  QUESTIONNAIRE_TURN_ATTACHMENT_MIME_TYPES,
  QUESTIONNAIRE_TURN_COMMAND_KINDS,
  isQuestionnaireChatTurnTerminal,
} from "./chat-turn";
export type {
  CancelQuestionnaireChatTurnResponse,
  EnqueueQuestionnaireChatTurnRequest,
  EnqueueQuestionnaireChatTurnResponse,
  QuestionnaireChatTurnReadback,
  QuestionnaireCollectedAssetOperation,
  QuestionnaireChatTurnSnapshot,
  QuestionnaireChatTurnFailureDetail,
  QuestionnaireChatTurnStage,
  QuestionnaireChatTurnStageEvent,
  QuestionnaireChatTurnStatus,
  QuestionnaireChatTurnStatusEvent,
  QuestionnaireTurnCommand,
  QuestionnaireTurnCommandKind,
  QuestionnaireTurnAttachmentInput,
  QuestionnaireTurnAttachmentKind,
  QuestionnaireTurnInput,
  SubscribeQuestionnaireDraftPayload,
} from "./chat-turn";
export type {
  CompileSurveyResponse,
  LaunchQuestionnaireResponse,
  QuestionnaireAppliedAsset,
  QuestionnaireAppliedAssetAspect,
  QuestionnaireAppliedChanges,
  QuestionnaireAppliedEffect,
  QuestionnaireAppliedQuestion,
  QuestionnaireAppliedQuestionAspect,
  QuestionnaireAppliedStudyFact,
  QuestionnaireChatMessage,
  QuestionnaireMessageAttachment,
  QuestionnaireChatMessageKind,
  QuestionnaireChatState,
  QuestionnaireChatStatus,
  QuestionnaireDraftApiResponse,
  QuestionnaireThreadDraftListItem,
  QuestionnaireDraftArtifacts,
  QuestionnaireMaterialReceipt,
  QuestionnaireReceiptAsset,
  QuestionnairePreviewPass,
  QuestionnairePreviewPassesResponse,
  QuestionnaireStudyIntent,
  QuestionnaireValidateResponse,
} from "./builder-wire";
export {
  CONDITION_RULE_CODES,
  QUESTIONNAIRE_CALL_PURPOSES,
} from "./validation-codes";
export type {
  ConditionRuleCode,
  QuestionnaireCallPurpose,
} from "./validation-codes";
export {
  ASSET_COMPARISON_VALIDATION_CODES,
  answersWithComparisonParticipants,
  assetComparisonCoherenceIssues,
  carriedListKeepsComparison,
  isAssetBackedChoice,
  isExactAssetRoster,
  removingOptionLeavesTooFewParticipants,
} from "./asset-comparison-coherence";
export {
  ASSET_MEDIA_REF_INTEGER_FIELDS,
  ASSET_MEDIA_REF_STRING_FIELDS,
  assetFileIds,
  mediaFileIds,
  hasUsableNonImageAssetMediaLocator,
} from "./asset-media-locator";
export type {
  AssetComparisonValidationCode,
  AssetComparisonValidationIssue,
} from "./asset-comparison-coherence";
export {
  ASSET_MENTION_TRAILING_LOOKAHEAD,
  assetMentionAliasPattern,
  buildAssetMentionAliases,
} from "./asset-mention-aliases";
export {
  assetMentionLabel,
  assetRenames,
  bindAssetMentionTokens,
  buildAssetMentionBindingTable,
} from "./asset-mentions";
export type { AssetMentionBindingTable, AssetRename } from "./asset-mentions";
export {
  assetMentionByKey,
  assetRefKeysIn,
  assetRefToken,
  hasAssetRefTokens,
  renderAssetRefTokens,
  replaceAssetRefTokens,
  splitAssetRefTokens,
} from "./asset-ref-tokens";
export {
  canonicalYoutubeWatchUrl,
  isYoutubeUrl,
  youtubeEmbedUrl,
  youtubeThumbnailUrl,
  youtubeVideoId,
} from "./youtube-url";
export {
  YOUTUBE_NON_KEYFRAMEABLE_MESSAGE,
  directVideoMimeType,
  isDirectVideoUrl,
  nonKeyframeableVideoMessage,
  unreadableVideoHost,
  unreadableVideoHostMessage,
} from "./video-url";
export type { AssetMentionAliasInput } from "./asset-mention-aliases";
export {
  formatProductPrice,
  formatTypedPrice,
  productVersionLabel,
  writesFormattedPrice,
} from "./product-version-label";
export type { PriceWording } from "./product-version-label";
export {
  arePriceVersionsOfOneProduct,
  priceVersionProductKey,
  priceVersionsOf,
} from "./price-versions";
export {
  priceVersionsPricedApart,
  assetDisplayNameOnWrite,
  assetHasOrdinal,
  assetOfRef,
  assetCatalogTitle,
  attachCapacityOf,
  attachItemsOf,
  authorAnswerAssetLabel,
  authorAssetLabel,
  exactChoiceAssetRefAnswerLabel,
  exposureStripAssetLabel,
  materialCardOf,
  materialOffers,
  newItemsCardOf,
  ownPriceVersion,
  partsAskingOnBuild,
  partsListedBesideCard,
  respondentAnswerAssetLabel,
  respondentFacingAssetLabel,
  respondentProseAssetLabel,
  resolveMaxAssetNameLength,
  shortenAssetDisplayName,
  shortenAssetDisplayNameToLimit,
  shouldBoundAssetDisplayName,
  studyMaterialFamilies,
  twinFacingAssetLabel,
} from "./asset-items";
export type {
  AttachTargetAsset,
  MaterialAttachOffer,
  MaterialOffers,
} from "./asset-items";
export { linksOnly, messageLinks } from "./message-links";
export { TESTED_ASSET_MAX_BY_TYPE } from "./asset-families";
export {
  DEFAULT_MAX_ASSET_NAME_LENGTH,
  MAX_ASSET_DESCRIPTION_WORDS,
  clampToMaxWords,
  countWords,
  isWithinAssetDescriptionWordLimit,
} from "./asset-description-limits";
export {
  ASSET_AUTHORED_METADATA_KEY,
  assetTextReachesRespondents,
  descriptionSourceProvenance,
  isAssetDescriptionPlaceholder,
  isAssetTextProvenance,
  nameSourceProvenance,
  readAssetTextProvenance,
} from "./asset-text-provenance";
export type { AssetTextProvenance } from "./asset-text-provenance";
export {
  CONTINUOUS_UNIT_IDS,
  CONTINUOUS_UNIT_REGISTRY,
  DEFAULT_CURRENCY_CODE,
  DURATION_EXPOSED_UNITS,
  LP_COMPOSITE_DIMENSIONS,
  LP_DIMENSION_DISPLAY_LABELS,
  QUESTION_TYPE_IDS,
  QUESTION_TYPE_REGISTRY,
  RATING_POLARITIES,
  RATING_UNIT_IDS,
  RATING_UNIT_REGISTRY,
  TESTED_ASSET_TYPE_IDS,
  PRODUCT_PRICE_CURRENCIES,
  UNIT_REGISTRY,
  getEffectiveCurrencyCode,
  getQuestionTypeRegistryEntry,
  getUnitRegistryEntry,
  declaresLpTopics,
  lpTopicOfTags,
  isContinuousUnitId,
  isQuestionType,
  isRatingUnitId,
  isUnitId,
  ratingBounds,
  ratingHasMidpoint,
  ratingMidpointLabel,
  questionTypeIdsForMode,
  surveyJsTargetOf,
  elementTypeOf,
  unitRequiresExplicitBoundsForComposite,
} from "./registry";
export type {
  LpComposite,
  LpCompositeDimension,
  LpCompositeDimensionTag,
  MatrixCellType,
  NativeDisplayFormat,
  NormalizationRecipe,
  QuestionSummaryMeasure,
  QuestionTypeContentContract,
  QuestionTypeMode,
  QuestionTypeRegistryEntry,
  SurveyJsTarget,
  SurveyJsVariant,
  UnitFamily,
  UnitRegistryEntry,
} from "./registry";
export {
  CAPABILITY_IDS,
  CAPABILITY_LIMITS,
  CAPABILITY_NEAREST_IDS,
  CAPABILITY_NEAREST_KEYS,
  CAPABILITY_WORDS_KEYS,
  capabilityHasNearest,
  capabilityIsLimit,
  capabilityAsksNothingToChange,
  capabilityNearestKey,
  capabilityWordsKey,
} from "./capabilities";
export type { CapabilityId, CapabilityNearestId } from "./capabilities";
export {
  ASSET_CHOICE_MIN_ASSETS,
  ASSET_CHOICE_SURFACE_QUESTION_TYPES,
  BRAND_COMPETITOR_MAX,
  CHOICE_LIST_QUESTION_TYPES,
  CHOICES_FROM_SOURCE_QUESTION_TYPES,
  CHOICES_FROM_TARGET_QUESTION_TYPES,
  MATRIX_QUESTION_TYPES,
  MIN_CHOICE_MEMBERS,
  OPTION_LIST_QUESTION_TYPES,
  RANKED_ITEMS_FLOOR,
  OPTION_ORDER_QUESTION_TYPES,
  ORDERED_ANSWER_QUESTION_TYPES,
  OUTCOME_CONDITION_SOURCE_QUESTION_TYPES,
  RATING_DESIGN_QUESTION_TYPES,
  SELECT_QUESTION_TYPES,
  SPLIT_SOURCE_QUESTION_TYPES,
  splitComplementOf,
  type SplitComplement,
  estimatedQuestionnaireMinutes,
} from "./question-capabilities";
export { testedAssetCapBreaches, testedAssetCountByType } from "./asset-caps";
export {
  RULE_EVALUATORS,
  assetNamesTokenized,
  assetTokensKnown,
  conditionFieldsRead,
  countWithinBounds,
  evaluateRule,
  fieldAllowedForType,
  operatorReadsSource,
  scaleExecutable,
  valueInList,
} from "./rule-evaluators";
export type {
  ConditionOperatorFamilies,
  QuestionCapabilityFlags,
  RatingScaleFactsByUnit,
  RuleEvaluatorKind,
  RuleFailure,
} from "./rule-evaluators";
export {
  AD_INSTRUMENT_MIN_RESEARCH_QUESTIONS,
  DEFAULT_RESEARCH_QUESTION_FLOOR,
  MAX_MATRIX_CATEGORIES,
  MAX_QUESTION_LABEL_LENGTH,
  MAX_MATRIX_MEASURES,
  RESEARCH_QUESTION_CEILING,
  isChoicesFromModeReachable,
  resolveResearchQuestionFloor,
  validateQuestionnaireDraft,
  validateQuestionnaireDraftPayload,
  // Rules and helpers the backend runtime validator runs as they are here
  // (apps/backend/src/questionnaires/common/runtime.ts), instead of a copy.
  addIssue,
  asArray,
  collectCompositeMetricIds,
  collectMatrixVisibilityRowValues,
  collectMetricIds,
  collectStableKey,
  conditionExprIssues,
  continuousThresholdBounds,
  hasGcsImageReference,
  hasMatrixRowsPlaceholder,
  hasText,
  heldBackReadIssues,
  isRecord,
  isVisibilityRuleGroupRecord,
  missingAssetContent,
  orderedShuffleField,
  rankingBounds,
  rankingCountIssues,
  rankingCountRange,
  rankingCountsHeld,
  selectionCountsHeld,
  rankingFixedLength,
  rankingMaxLength,
  responseOf,
  routingRuleIssues,
  studyObjectiveIssues,
  designBriefIssues,
  objectiveMarkupIssues,
  validateAssetBindings,
  validateAssetRefs,
  validateCompositeBounds,
  validateCompositeReferences,
  validateNotApplicableLabel,
  validateOpenEndCoverage,
  validateRatingPointLabels,
} from "./validator";
export type {
  DraftValidationResult,
  RankingBounds,
  RankingCountField,
  RankingCountIssue,
} from "./validator";
export { countQuestionnaireResearchQuestions } from "./research-question-count";
export {
  acceptedCanonicalSurveyConfigStructureIssues,
  acceptedCanonicalSurveyElementStructureIssues,
  acceptedDesignBriefStructureIssues,
  acceptedQuestionnaireArtifactStructureIssues,
  acceptedTestedAssetBlockStructureIssues,
  exactSubjectShapeIssues,
  productPriceStructureIssues,
  readProductPrice,
} from "./accepted-artifact-validator";
export {
  acceptedDraftRelationIssues,
  acceptedQuestionnaireArtifactRelationIssues,
  CURRENT_GENERATED_METRIC_CAPABILITIES,
  liftHalvesShareScale,
  liftPairScaleMismatch,
  metricCapabilityIssues,
  followUpRelationIssues,
  responseRelationIssues,
} from "./accepted-artifact-relations";
export { gridRowsRandomized } from "./grid-row-order";
// C1.1 PLAN contract. Mirrored in apps/python-api/models/questionnaire_plan.
export type {
  EvidenceModule,
  ExcludedModule,
  PlanAnalysisUse,
  PlanAnalyticalApproach,
  PlanAnalyticalRole,
  PlanAllowedOutcome,
  PlannedVisibilityRule,
  PlanVisibilityCondition,
  PlannedRouteCondition,
  PlannedRoutingRule,
  PlanRouteLeaf,
  PlanAssetChoiceIntent,
  PlanAssetScope,
  PlanOptionOrder,
  PlanSelectionPolicy,
  PlanSelectionPolicyBounded,
  PlanSelectionPolicyKeepExisting,
  PlanSelectionPolicyUnbounded,
  PlanOpenEndAnalysisFrame,
  PlanCustomReportDisposition,
  PlanCustomReporting,
  PlanCustomSurveyDataOnlyDisposition,
  PlanLiftHalf,
  PlanLiftIntent,
  PlanMatrixIntent,
  PlanMatrixIntentFixed,
  PlanMatrixMeasureAllowedOutcomes,
  PlanMatrixIntentTestedAssets,
  PlanMatrixRowSource,
  PlanMatrixTargetShape,
  PlanExposureMarks,
  PlanModuleCoverage,
  PlanRecipeIdentity,
  PlanProfilingIntent,
  PlanProfilingPurpose,
  PlanProfilingRole,
  PlanScaleFamily,
  PlanVerbatimContent,
  PlannedTopic,
  QuestionnairePlan,
  SharedList,
  SharedListMember,
} from "./plan";
export {
  questionnairePlanExposureMarksResolve,
} from "./plan-validator";
// C3.3 profiling tag vocabulary. Mirrored in
// apps/python-api/models/questionnaire/profiling.py.
export {
  CUT_ELIGIBLE_PROFILING_ROLES,
  PLAN_SEGMENTATION_CUT_TAG,
  PLAN_USE_TAG_PREFIX,
  PROFILE_TAGS,
  PROFILE_TAG_PREFIX,
  PROFILE_ZONE_BY_ROLE,
  PROFILING_ROLES,
  isCutEligible,
  parseProfileTag,
  profileTag,
  profilingRoleOf,
} from "./profiling";
// C1.3 planning policy and PLAN request envelope. Mirrored in
// apps/python-api/models/questionnaire_plan/request.py.
export type {
  PlanActiveContextGates,
  PlanAdAssetFacts,
  PlanAdPlacement,
  PlanAssetFactsBase,
  PlanAudienceContext,
  PlanAudienceDimensionDistribution,
  PlanAudienceDistributionSegment,
  PlanAudienceUniversalAttribute,
  PlanBrandAssetFacts,
  PlanConceptAssetFacts,
  PlanDraftOrigin,
  PlanFollowUpBudget,
  PlanLandingPageAssetFacts,
  PlanLandingPageGate,
  PlanMessageAssetFacts,
  PlanMessageClaimType,
  PlanMethodologyFamily,
  PlanOrganizationContext,
  PlanPackagingAssetFacts,
  PlanPainAssetFacts,
  PlanProductAssetFacts,
  PlanProductAssetPriceFacts,
  PlanProvenanceRef,
  PlanRecipeRef,
  PlanRequestIdentity,
  PlanRequestMode,
  PlanTestedAssetFacts,
  QuestionnaireConversationMessage,
  QuestionnaireConversationRequestPart,
  QuestionnairePlanContextV1,
  QuestionnairePlanRequestV1,
  QuestionnairePlanningPolicyV1,
  VideoSceneManifest,
  VideoSceneShot,
} from "./plan-request";
// C2.5a AUTHOR/assembly request. Mirrored in
// apps/python-api/models/questionnaire_author/request.py.
export type {
  AuthorBrandRosterEntryV1,
  AuthorLeftOutAssetV1,
  AuthorDesignBriefFieldsV1,
  QuestionnaireAuthorDraftRequestV1,
} from "./author-request";
// C3.2 final recipe coverage. Produced by deterministic assembly after all
// canonical questions exist; mirrored by the Python AUTHOR result.
export type { FinalDraftRecipeCoverage } from "./author-result";
// PLAN's typed catalog operations. Mirrored in
// apps/python-api/models/questionnaire_plan/catalog_operations.py.
export type {
  RePlanAssetOperation,
  RePlanSharedContextOperation,
} from "./replan-request";
export type { RePlanUnusedFile } from "./plan.generated";

export { QUESTIONNAIRE_CHAT_MAX_ASSISTANT_FOLLOW_UPS } from "./chat-budget";

export type StudyAuthoringMode = "custom";

export type QuestionnaireDraftStatus =
  | "collecting_problem"
  | "asking_clarifying_questions"
  | "draft_ready"
  | "editing"
  | "assets_required"
  | "ready_for_review"
  | "launched"
  | "archived"
  | "duplicating"
  | "duplicate_failed";

/**
 * Why a draft takes no edits (B13): a status a lifecycle command owns, or an
 * older draft stored before the accepted-bundle authority. Null on an
 * editable draft. The builder reads it on load and locks, instead of learning
 * it from a refused Save.
 */
export type QuestionnaireDraftNotEditableReason =
  | "launched"
  | "archived"
  | "duplicating"
  | "duplicate_failed"
  | "legacy_read_only";

export type ArtifactGenerator = "chat" | "manual" | "system";

export type QuestionCreatedBy = "chat" | "user" | "system";

export type AssumptionSource = "chat" | "system" | "user_confirmed";

export type AssumptionAffects =
  | "questions"
  | "assets"
  | "metrics"
  | "slides"
  | "audience";

// Keep in sync with AudienceSelectionRef in apps/python-api/models/questionnaire/contracts.py —
// both sides must accept the same audience fields (planner metadata is persisted on drafts).
export type QuestionType =
  | "single_select"
  | "multi_select"
  | "continuous"
  | "rating"
  | "open_end"
  | "matrix_rating"
  | "matrix_categorical"
  | "ranking";

export type QuestionRole =
  | "research"
  | "asset_evaluation"
  | "asset_follow_up"
  | "screening";

export type RatingUnitId =
  | "rating_1_5"
  | "rating_1_7"
  | "rating_1_10"
  | "rating_0_10"
  | "rating_0_100"
  | "binary_yes_no";

/** The continuous units, owned by `config/questionnaire-vocabularies.json`. */
export type ContinuousUnitId = import("./vocabularies").ContinuousUnitId;


export type MatrixRows = FixedMatrixRows | TestedAssetsMatrixRows;

export type ResponseDefinition =
  | SingleSelectResponse
  | MultiSelectResponse
  | ContinuousResponse
  | RatingResponse
  | OpenEndResponse
  | MatrixRatingResponse
  | MatrixCategoricalResponse
  | RankingResponse;

/**
 * Stable PLAN identity stamped onto a canonical question by assembly.
 *
 * `topicKey` joins the final question to its planned brief.
 * `recipeModuleId` is present exactly when that topic measures a recipe module.
 * Version 3 writes exactly one current disposition: `recipeModuleId` or
 * `customReporting`. `reportingTargetModuleId` and `surveyDataOnlyReason` are
 * version-2 aliases retained only for historical draft readers. Coverage reads
 * only typed identities; it never infers them from construct names, tags,
 * stable keys, or respondent-facing wording.
 */
export type QuestionCustomReporting =
  | QuestionCustomReportDisposition
  | QuestionCustomSurveyDataOnlyDisposition;

// Additive, schema/compiler-level skip-logic capability (see
// docs/questionnaire-builder-refactor/contracts/canonical-questionnaire.md).
// Authored by the chat planner, the manual builder UI's condition editor, and
// directly on draft JSON (API/scripts).
// The operators, their families and the question types each reads are
// `config/questionnaire-condition-operators.json` (./condition-operators):
// choice operators read a single select, a multi select or one categorical
// matrix cell (`equals`/`not_equals` not a multi select, whose answer is a
// set) and need non-empty `values` (exactly one for `equals`/`not_equals`;
// the registry's `values`). Numeric operators read a rating, a number
// or one matrix-rating cell and need a finite `threshold` (within bounds when
// the dependency defines them); `between` also requires `thresholdHigh`
// (inclusive upper bound, `threshold <= thresholdHigh`). Matrix deps also
// require `matrixRowValue` (and `matrixMeasureId` when the matrix has more
// than one measure). Ranking operators read a ranking and need non-empty
// `values` and integer `rankK`. `is_not_applicable` reads the declared
// non-scored state of an earlier rating or one matrix-rating cell whose
// selected measure declares that state. It never carries the compiler's
// internal response value.
// Keep in sync with QuestionVisibilityRule in
// apps/python-api/models/questionnaire/contracts.py.
export type {
  ConditionOperatorFamily,
  ConditionPolarity,
  ConditionValueCount,
  VisibilityRuleChoiceOperator,
  VisibilityRuleNotApplicableOperator,
  VisibilityRuleNumericOperator,
  VisibilityRuleOperator,
  VisibilityRuleRankingOperator,
} from "./condition-operators";
export {
  CHOICE_CONDITION_OPERATORS,
  CONDITION_OPERATORS,
  MAX_VISIBILITY_RULE_GROUP_SIZE,
  NOT_APPLICABLE_CONDITION_OPERATORS,
  NUMERIC_CONDITION_OPERATORS,
  RANKING_CONDITION_OPERATORS,
  conditionFamilyReads,
  conditionMembershipOperator,
  conditionOperatorFamily,
  conditionOperatorPolarity,
  conditionOperatorReads,
  conditionOperatorValueCount,
  isConditionOperator,
} from "./condition-operators";
export type VisibilityRuleJoin = "and" | "or";

export type QuestionVisibilityRuleExpr =
  | QuestionVisibilityRule
  | QuestionVisibilityRuleGroup;


export function isVisibilityRuleGroup(
  value: unknown,
): value is QuestionVisibilityRuleGroup {
  if (!value || typeof value !== "object") {
    return false;
  }
  const record = value as Record<string, unknown>;
  return (
    (record.join === "and" || record.join === "or") &&
    Array.isArray(record.rules)
  );
}

export function getVisibilityRuleLeaves(
  expr: QuestionVisibilityRuleExpr | null | undefined,
): Array<QuestionVisibilityRule> {
  if (!expr) {
    return [];
  }
  if (isVisibilityRuleGroup(expr)) {
    return expr.rules;
  }
  return [expr];
}

/**
 * Control-flow action after the host question is answered.
 * Keep in sync with QuestionRoutingAction in
 * apps/python-api/models/questionnaire/contracts.py.
 */
export type QuestionRoutingAction = "skip_to" | "end_survey";

export type TestedAssetType =
  | "concept"
  | "ad"
  | "landing_page"
  | "product"
  | "packaging"
  | "message"
  | "brand"
  | "pain";

export type AssetMediaKind = "image" | "video" | "document" | "url_snapshot";

export interface LandingPageAnalysisMetadata {
  pricingVisible?: boolean | null;
  analyzedAt?: string;
  /**
   * What the read read: the page's images, or its pasted copy. Absent on a
   * read stored before text reads: images.
   */
  readFrom?: "images" | "text";
  /**
   * A text read only: the digest of the copy it read. A different current
   * copy makes the read stale (the backend's `landingPageRead`).
   */
  sourceDigest?: string;
}

export type TestedAsset =
  | ConceptAsset
  | AdAsset
  | LandingPageAsset
  | ProductAsset
  | PackagingAsset
  | MessageAsset
  | BrandAsset
  | PainAsset;

/**
 * The backend's authoritative verdict on whether a persisted draft can compile
 * and reach review. Response-only: it is derived from the persisted artifacts
 * on every read/write and is never accepted from a client or written to
 * storage.
 *
 * Clients consume `isReviewEligible`, `canCompile` and `blockingIssues` and
 * MUST NOT re-derive any of them. In particular they must not compare against
 * `resolvedMinimum` or any local count constant: the backend is the authority
 * (see `resolveResearchQuestionFloor` in ./validator, which the backend runtime
 * re-exports; a constant 1 today, but the client still reads the verdict
 * rather than the number).
 */
export interface QuestionnaireDraftReadiness {
  schemaVersion: "questionnaire-draft-readiness.v1";
  /**
   * Identifies the exact draft bytes this verdict describes, so a verdict can
   * never be applied to a draft it did not evaluate. Stable across key order
   * and insensitive to `updatedAt`/`validation` re-stamping.
   */
  draftFingerprint: string;
  researchQuestionCount: number;
  /**
   * Telemetry and tests only. Deliberately opaque to product code: never
   * render it and never gate on it. Rendered text comes from `blockingIssues`.
   */
  resolvedMinimum: number;
  isReviewEligible: boolean;
  canCompile: boolean;
  /** Error-severity issues that block compile/launch. Safe to render. */
  blockingIssues: Array<QuestionnaireValidationIssue>;
  /** Warning-severity issues. Never block; surfaced as guidance. */
  advisoryIssues: Array<QuestionnaireValidationIssue>;
}

export interface AssetRequirementsSchema {
  required: boolean;
  assetTypes: Array<TestedAssetType>;
  minAssets: number;
  requiredFieldsByType: Record<TestedAssetType, Array<string>>;
  recommendedFieldsByType: Record<TestedAssetType, Array<string>>;
}

/**
 * Generated from `MetricKind` in apps/python-api/models/questionnaire/contracts.py
 * (`../reporting/metric-kinds.generated.ts`); the runtime list is `METRIC_KINDS`.
 */
export type MetricKind = GeneratedMetricKind;

export type MetricSource =
  | QuestionMetricSource
  | MatrixMeasureMetricSource
  | MatrixRowMetricSource
  | AssetMeasureMetricSource;

/**
 * T4 — open-end classifier kind. Selects the per-respondent classification +
 * aggregation algorithm the canonical runtime applies to an open-end analysis.
 */
export type OpenEndClassifierKind =
  | "comprehension"
  | "objection_severity"
  | "affect"
  | "improvement_themes";

/**
 * Generated from `CanonicalSlideType` in apps/python-api/models/questionnaire/contracts.py
 * (`../reporting/canonical-slide-types.generated.ts`); the runtime list is
 * `CANONICAL_SLIDE_TYPES` (`./canonical-vocabularies`).
 */
export type CanonicalSlideType = GeneratedCanonicalSlideType;

export type SlideRole =
  | "opener"
  | "summary"
  | "question_evidence"
  | "comparison"
  | "recommendation"
  | "outro";

/**
 * D3. The named place a qualitative slide holds in its recipe's report story,
 * beyond its tone (which `slideType` types). Declared by the recipe module,
 * carried on `ReportingPlan.moduleQualStoryRoles` and stamped on the sequenced
 * slide; the deck titles the slide from it.
 */
export type QualStoryRole = "first_impression" | "friction" | "what_works";

/**
 * D3. The statistic that leads a recipe module's KPI row, declared by the
 * module instead of inferred from the answer's shape.
 */
export type LeadStatistic =
  | "favourable_share"
  | "modal_share"
  | "rank_index"
  | "median";

// Mirrors DeckWarning.code in apps/python-api/canonical_deck/models.py. It had
// already drifted by two members before this; keep it in step when adding one.
export type CanonicalDeckWarningCode =
  | "uncovered_question"
  | "question_not_reached"
  | "missing_required_metric"
  | "missing_required_qual"
  | "low_confidence_qual"
  | "qual_evidence_unavailable"
  | "qual_evidence_omitted"
  | "lift_not_drawn"
  | "winner_tie"
  | "quadrant_axis_fallback"
  | "quadrant_plan_backfill"
  | "podium_skipped"
  | "slide_content_overflow"
  | "duplicate_quote_omitted"
  | "duplicate_quote_kept"
  | "lp_exhibit_dropped"
  | "planned_exhibit_unrendered"
  | "price_curve_slides_condensed"
  | "duplicate_exhibit_omitted"
  | "storyline_omitted"
  | "composition_question_omitted"
  | "study_low_base"
  | "study_findings_withheld"
  | "empty_group"
  | "mixed_media_tested"
  | "slide_text_cut"
  | "scale_anchor_overflow"
  | "plot_invalid";

export interface DeckWarning {
  code: CanonicalDeckWarningCode;
  severity: "info" | "warning" | "error";
  message: string;
  slideId?: string;
  questionId?: string;
  metricId?: string;
}

export interface DeckCoverageReport {
  researchQuestionCount: number;
  coveredQuestionIds: Array<string>;
  uncoveredQuestionIds: Array<string>;
  slideCount: number;
  warnings: Array<string>;
}

export interface CanonicalDeckSlide {
  id: string;
  type: string;
  canonicalSlideType: CanonicalSlideType;
  title?: string | null;
  questionIds: Array<string>;
  metricIds: Array<string>;
  qualitativeAnalysisIds: Array<string>;
  compositeMetricIds: Array<string>;
  trace: { chartSegmentDefault?: ResolvedChartSegmentDefault; [key: string]: Array<string> | ResolvedChartSegmentDefault | undefined };
  [key: string]: unknown;
}

// Mirrors DeckSlideFailure in apps/python-api/canonical_deck/models.py: one
// plan item whose slides could not be built and are not in the deck. Never
// rendered to readers.
export interface DeckSlideFailure {
  slideId: string;
  canonicalSlideType: CanonicalSlideType;
  questionIds: Array<string>;
  stage: "build" | "renderer_contract";
  errorType: string;
  errors: Array<string>;
}

export interface CanonicalDeckOutput {
  schemaVersion: "canonical-deck.v1";
  registryVersion: "questionnaire-registry.v1";
  experimentId: string;
  questionnaireDraftId: string;
  slides: Array<CanonicalDeckSlide>;
  coverage: DeckCoverageReport;
  warnings: Array<DeckWarning>;
  // Absent on a deck stored before slide failures were recorded.
  failedSlides?: Array<DeckSlideFailure>;
  // BCP-47 tag of the deck language, for number, percent and currency
  // formatting (plots plan, task C). Absent on decks stored before the field.
  locale?: string | null;
}

/**
 * The study's agreed objective. Mirrors the Quick Study pydantic `StudyGoals`
 * (apps/python-api/quick_study/models.py) field-for-field so the two products
 * share one shape.
 *
 * `decision` is the one line the study informs ("Which landing page drives more
 * sign-ups"). `learn` are the neutral things it will surface, never "confirm V2
 * wins". `source` tracks provenance: inferred by the agent, confirmed by the
 * marketer, or edited by them.
 */
export type StudyEvidenceRequiredness = "required" | "optional" | "unspecified";

export type StudyEvidenceTopicCardinality = "one" | "one_or_more";

export type StudyEvidenceReporting =
  | "report"
  | "survey_data_only"
  | "unspecified";

export type StudyEvidenceExpansionPolicy =
  | "requested_only"
  | "recommendations_allowed";

export type StudyEvidenceAnswerKind =
  | "choice"
  | "multiple_choice"
  | "scale"
  | "number"
  | "open_text"
  | "ranking";

export type StudyEvidenceAllowedOutcome =
  | "none"
  | "cannot_tell"
  | "not_applicable"
  | "prefer_not_to_answer";

export type StudyEvidenceRole =
  | "context"
  | "occurrence"
  | "evaluation"
  | "selection"
  | "explanation"
  | "other";

export type StudyEvidenceSubjectScope =
  | "study"
  | "each_named_brand"
  | "each_tested_asset"
  | "tested_asset_set";

/**
 * Question shapes a user named explicitly (`targetShape`). Stored on an
 * accepted evidence constraint as `IntakeAuthoringTargetShape`; read by
 * `isStudyEvidenceContract` (`study-evidence-validator.ts`). The names keep
 * their historical `Intake` prefix because stored evidence and the Python
 * contract (`models/questionnaire/contracts.py`) use them.
 */
export type IntakeQuestionTypeHint =
  | "matrix"
  | "matrix_rating"
  | "matrix_categorical"
  | "rating"
  | "single_select"
  | "multi_select"
  | "open_end"
  | "ranking"
  | "number"
  | "continuous";

export type IntakeRowSourceHint = "fixed" | "tested_assets";

export type IntakeOrientationHint = "rows_are_subjects" | "rows_are_measures";

/**
 * Exposure design a message explicitly stated: each stimulus evaluated in an
 * isolated session (`monadic`) vs shared exposure during evaluation
 * (`comparison`). Never inferred; absent means the message said nothing. It
 * is not part of a stored constraint's shape (`IntakeAuthoringTargetShape`).
 */
export type IntakeExposureHint = "monadic" | "comparison";

/** Durable question-authoring shape. Exposure and baseline have a separate owner. */
export interface IntakeAuthoringTargetShape {
  questionType?: IntakeQuestionTypeHint;
  questionCount?: number;
  columnCount?: number;
  measureCount?: number;
  rowSource?: IntakeRowSourceHint;
  rowCount?: number;
  orientation?: IntakeOrientationHint;
  pointLabels?: IntakePointLabelIntent;
}

export { isStudyEvidenceContract } from "./study-evidence-validator";

/**
 * The `decisionIsFallback` stamp a client-side objective edit may carry
 * forward, for the optimistic writes behind the questionnaire panel's
 * objectives row (the workspace draft, the pre-draft session, the panel's own
 * pending copy).
 *
 * The backend re-derives the stamp from its own fallback map, which the browser
 * has no copy of. What the browser CAN tell is whether the sentence still is
 * the one the stamp was made about: a decision the marketer retyped is theirs,
 * so the stamp is dropped. Returns the key either way, so spreading the result
 * over a previous objective clears a stale stamp instead of inheriting it.
 */
export function carriedObjectiveFallbackStamp(
  previous:
    | Pick<StudyGoals, "decision" | "decisionIsFallback">
    | null
    | undefined,
  nextDecision: string,
): { decisionIsFallback: true | undefined } {
  const carried =
    previous?.decisionIsFallback === true &&
    (previous.decision ?? "").trim() === (nextDecision ?? "").trim();
  return { decisionIsFallback: carried ? true : undefined };
}

/**
 * One cap for the whole study objective: decision + learn bullets share this
 * budget (Fernando, 2026-08-09). A single editor gets a single limit, so there
 * are deliberately no per-field caps.
 *
 * `config/questionnaire-text-limits.json` owns what an objective can hold
 * (`studyObjectiveMaxChars`, `studyObjectiveMaxBullets`, the most learning
 * objectives: more is a list, not a study): the builder row's validator and
 * counter, Nest's objective write, `normalizeStudyGoals` and Python's PLAN
 * read it. A build writes
 * the objective too, the decision and one purpose per evidence module, so
 * PLAN holds a build to all but one line of it: at most
 * `STUDY_OBJECTIVE_MAX_BULLETS - 1` purposes (its provider schema's
 * `maxItems`), each line at most
 * `STUDY_OBJECTIVE_MAX_CHARS / (STUDY_OBJECTIVE_MAX_BULLETS + 1)` characters
 * (its prompt's budget).
 * The marketer can always add a learning objective after a build. No consumer
 * needs a smaller number (the objective is prompt context, never a fixed box);
 * this one makes that line 300 characters, above the longest line PLAN wrote
 * unbounded (235).
 */
export {
  COVERAGE_RATIONALE_MAX_CHARS,
  DESIGN_BRIEF_LINE_MAX_CHARS,
  DESIGN_BRIEF_METHOD_MAX_CHARS,
  STUDY_NAME_MAX_CHARS,
  STUDY_OBJECTIVE_MAX_BULLETS,
  STUDY_OBJECTIVE_MAX_CHARS,
  VIDEO_SCENE_MAX_SHOTS,
  VIDEO_SCENE_SHOT_MAX_CHARS,
  textLength,
} from "./text-limits";

/**
 * How the objective router classified the objective it was given. The same
 * shape of judgment the classifier's `statesDecision` makes, with the extra
 * outcomes routing needs.
 *
 * - `routed`   the platform serves the objective as it was asked.
 * - `reframed` the objective asked for something the capability manifest
 *   forbids (recall, a named pricing method, lift against a baseline) and the
 *   brief carries the nearest measurable question plus a caveat saying so.
 * - `authored` the objective was missing or unusable, so the router wrote the
 *   one the marketer should have given and routed that.
 * - `custom`   valid, buildable, and no standard path fits.
 */
export type DesignBriefClassification =
  | "routed"
  | "reframed"
  | "authored"
  | "custom";

/**
 * Instrument choice for multi-ad head-to-head: each ad rated on its own, in
 * a lane per ad that every person answers (`monadic`), vs everyone compares
 * all ads (`comparison`). Stored on the draft as a
 * sibling of `designBrief`, never inside the persisted brief.
 *
 * Contract: an absent value means comparison, everywhere this is read.
 * `"monadic"` is reachable two ways: a draft that already stored it, or a
 * turn whose message explicitly stated a monadic exposure design
 * (`IntakeTargetShape.exposure`, grounded against the raw message). A read of
 * the conversation (`DesignBrief.exposure`, PLAN's) never writes it: the
 * StudySpec records that read as the router's. Nothing else writes it, and no
 * inference ever does.
 */
export type AdDesignPreference = "monadic" | "comparison";

/**
 * An objective change the PLANNER declared on this turn.
 *
 * A declaration channel, not a mutation (Wave 9 D6). `draft.studyGoals` is
 * state the planner carries through untouched, and before the first build the
 * captured objective always wins inside it, so an objective changed anywhere
 * else is dropped and the drop is spoken aloud. This field is the only way the
 * agent can change the objective, which is what lets its presence mean exactly
 * one thing: the user asked for the change.
 *
 * Applied on ANY turn, with a draft or with only a clarifying question, to both
 * stored copies (the draft and the chat state), sanitized and clamped the way
 * the objective PATCH endpoint does.
 *
 * Present only when declared: python strips the whole field when the decision
 * is empty. It lives on the planner RESPONSE and is never persisted, so it
 * carries no launch-snapshot risk.
 */
export interface StudyObjectiveUpdate {
  /** The call the study now informs. Non-empty by construction. */
  decision: string;
  /** Always present, may be empty. Capped at STUDY_OBJECTIVE_MAX_BULLETS. */
  learn: Array<string>;
  /** One short line naming what the user said that asked for it. May be empty. */
  reason: string;
}

/**
 * The versioned record of what a recipe-backed plan selected and excluded.
 *
 * A projection of `PlanModuleCoverage` plus the count that decision justified,
 * stored on the draft so review, repair and resumed drafts read one record
 * rather than re-deriving the scope from wording.
 *
 * `justifiedQuestionCount` is the plan's own number, and it may only ever LOWER
 * the draft-derived floor. A recipe-backed plan is allowed to justify fewer
 * questions than the generic minimum because its omissions are on the record;
 * it is not allowed to invent a stricter one.
 */
export const QUESTIONNAIRE_PLAN_COVERAGE_SCHEMA_VERSION =
  "questionnaire-plan-coverage.v1";

/**
 * Is this a plan-coverage record the WRITE path could have stamped?
 *
 * The one authority for that question, shared by both floor resolvers so the
 * frontend and the backend cannot disagree about whether a draft's floor is
 * lowered — a disagreement the marketer would see as a draft the builder calls
 * valid and the server rejects, or worse, the reverse.
 *
 * Strict on every field `buildPlanCoverageRecord` writes, not just the number
 * the floor reads. A record is a claim that a recipe-backed plan justified this
 * many questions, and the claim is only meaningful if the recipe it names is
 * there to check it against. Accepting a bare `{justifiedQuestionCount: 0}` —
 * which both resolvers used to do — let any caller posting a draft body drive
 * the question floor to zero and ship an empty questionnaire past validation.
 */
export function isValidPlanCoverageRecord(
  value: unknown,
): value is QuestionnairePlanCoverageRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  const isNonEmptyText = (candidate: unknown): boolean =>
    typeof candidate === "string" && candidate.trim().length > 0;
  const isTextArray = (candidate: unknown): boolean =>
    Array.isArray(candidate) &&
    candidate.every((entry) => typeof entry === "string");
  return (
    record.schemaVersion === QUESTIONNAIRE_PLAN_COVERAGE_SCHEMA_VERSION &&
    isNonEmptyText(record.recipeId) &&
    isNonEmptyText(record.recipeVersion) &&
    typeof record.justifiedQuestionCount === "number" &&
    Number.isInteger(record.justifiedQuestionCount) &&
    record.justifiedQuestionCount >= 0 &&
    isTextArray(record.moduleUniverse) &&
    isTextArray(record.selectedModules) &&
    Array.isArray(record.excludedModules) &&
    record.excludedModules.every(
      (entry) =>
        typeof entry === "object" &&
        entry !== null &&
        !Array.isArray(entry) &&
        isNonEmptyText((entry as Record<string, unknown>).moduleId) &&
        isNonEmptyText((entry as Record<string, unknown>).reason),
    )
  );
}

/**
 * The two declared exposure marks on an instrument, and where they came from.
 *
 * Phase 4b of the exposure-design generalization. Until this contract existed,
 * nothing on the draft SAID where the reveal was: one place computed it (PLAN's
 * `derive_post_reveal`, from the recipe binder's topic index) and every reader
 * downstream re-inferred it from whichever questions happened to carry
 * `postReveal`. A reorder, a hand-added question or a plan with no binder left
 * an untagged question exposed to the asset catalog from question one.
 *
 * Two named anchors, not one, and not a flag per question:
 *
 * - `reveal` (mark 1) is the LAST pre-exposure question. It and everything above
 *   it are asked before the twin sees any tested asset; everything after it is
 *   post-reveal. A `null` `stableKey` with a source is a DECLARED "nothing is
 *   pre-exposure": the reveal precedes question one.
 * - `sharedView` (mark 2) is the FIRST all-versions question. From it on, every
 *   twin sees every version together. A `null` `stableKey` with a source is a
 *   declared "no shared view": lanes run to the end.
 *
 * Provenance is PER MARK, because the two are set independently: the agent can
 * author the reveal on a Build while the user drags the lane exit, and each
 * survives or conflicts on its own. `source: "agent"` when Build or RePlan wrote
 * it; `source: "user"` when a person set or moved it.
 *
 * The whole object absent means NOT YET DECLARED (every draft persisted before
 * this contract, and a hand-built draft before its first normalize).
 * `normalizeDraft` derives both marks once and stores them, so absent never
 * survives a normalize.
 *
 * `postReveal` on `QuestionDefinition` becomes a derived OUTPUT of `reveal` on
 * both sides of the wire. It is never authored again.
 */
export const QUESTIONNAIRE_EXPOSURE_BOUNDARY_SCHEMA_VERSION =
  "questionnaire-exposure-boundary.v1";

export type QuestionnaireExposureMarkName = "reveal" | "sharedView";

export type QuestionnaireExposureMarkSource = "agent" | "user";

/**
 * Where a question sits relative to the two marks.
 *
 * Two zones and one line was the revision-3 model; the revision-4 lane exit
 * makes it three named positions with the same single temporal line. `pre` is
 * before any asset exists for the twin; `lane` is exposed, one version per twin
 * when the design has teeth; `shared_view` is every version at once.
 *
 * Scope stays a per-question property (`assetRefs[].role`, `assetBinding.mode`)
 * that the engine buckets on. The zone is what DRIVES that property on a
 * declared draft, never a second copy of it.
 */
export type QuestionnaireExposureZone = "pre" | "lane" | "shared_view";

/** Index pair the zone function reads. Resolved against one question list. */
export interface QuestionnaireExposureMarkIndexes {
  /** Index of `reveal.stableKey`; -1 when the mark is null or unresolvable. */
  revealIndex: number;
  /** Index of `sharedView.stableKey`; the question count when null. */
  sharedViewIndex: number;
}

function isExposureMark(value: unknown): value is QuestionnaireExposureMark {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const mark = value as Record<string, unknown>;
  const keys = Object.keys(mark).sort();
  if (
    keys.length !== 3 ||
    keys[0] !== "source" ||
    keys[1] !== "stableKey" ||
    keys[2] !== "updatedAt"
  ) {
    return false;
  }
  const hasKey =
    mark.stableKey === null ||
    (typeof mark.stableKey === "string" && mark.stableKey.trim().length > 0);
  return (
    hasKey &&
    (mark.source === "agent" || mark.source === "user") &&
    typeof mark.updatedAt === "string" &&
    mark.updatedAt.trim().length > 0
  );
}

/**
 * Is this an exposure boundary the WRITE path could have stamped?
 *
 * Strict on every field, for the same reason `isValidPlanCoverageRecord` is: a
 * boundary is a claim about what twins were shown, and a half-formed one read
 * as authoritative would silently move the line. Anything that fails here is
 * treated as "not declared" and re-derived, which is safe; nothing is repaired
 * in place.
 */
export function isValidExposureBoundary(
  value: unknown,
): value is QuestionnaireExposureBoundary {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return false;
  }
  const boundary = value as Record<string, unknown>;
  const keys = Object.keys(boundary).sort();
  if (
    keys.length !== 3 ||
    keys[0] !== "reveal" ||
    keys[1] !== "schemaVersion" ||
    keys[2] !== "sharedView"
  ) {
    return false;
  }
  if (
    boundary.schemaVersion !== QUESTIONNAIRE_EXPOSURE_BOUNDARY_SCHEMA_VERSION
  ) {
    return false;
  }
  const marksAreValid = (["reveal", "sharedView"] as const).every(
    (name) => boundary[name] === null || isExposureMark(boundary[name]),
  );
  return marksAreValid && "reveal" in boundary && "sharedView" in boundary;
}

/**
 * Resolve both marks to indexes against ONE ordered research-question list.
 *
 * An index is meaningful against the list it was resolved from and no other, so
 * this never caches: every caller resolves against the list it is about to read.
 *
 * A mark whose `stableKey` names a question that is not in the list resolves the
 * same way a null mark does (`-1` / `questionCount`). That is the safe reading:
 * a dropped anchor widens nothing and lanes nothing it should not, and the
 * re-plan road reports the drop as its own conflict rather than silently
 * re-anchoring here.
 */
export function resolveExposureMarkIndexes(
  questions: ReadonlyArray<{ stableKey?: string | null }>,
  boundary: QuestionnaireExposureBoundary | null | undefined,
): QuestionnaireExposureMarkIndexes {
  const questionCount = questions.length;
  const indexOfKey = (stableKey: string | null | undefined): number => {
    if (typeof stableKey !== "string" || stableKey.length === 0) {
      return -1;
    }
    return questions.findIndex((question) => question.stableKey === stableKey);
  };
  const revealIndex = boundary ? indexOfKey(boundary.reveal?.stableKey) : -1;
  const sharedViewRaw = boundary
    ? indexOfKey(boundary.sharedView?.stableKey)
    : -1;
  return {
    revealIndex,
    sharedViewIndex: sharedViewRaw < 0 ? questionCount : sharedViewRaw,
  };
}

/** `zone(i)` from §2.3: pre at or above the reveal, shared view at or below mark 2. */
export function exposureZoneAt(
  index: number,
  indexes: QuestionnaireExposureMarkIndexes,
): QuestionnaireExposureZone {
  if (index <= indexes.revealIndex) {
    return "pre";
  }
  if (index >= indexes.sharedViewIndex) {
    return "shared_view";
  }
  return "lane";
}

/**
 * The marks once the question at `stableKey` leaves its place (lead decision,
 * QA3 F11 review): moving a question never moves a line. A mark names the
 * question it sits on, so when that question moves, the mark is re-anchored
 * to the question now on the same side of the same gap: the reveal to the
 * question above it (null at the top), the shared view to the one below it
 * (null at the bottom). Call it before the question is taken out; `questions`
 * is the order it leaves. The mark keeps its provenance: the line is the one
 * already declared. A line moves only by an explicit line move, or by an added
 * or moved question's stated zone at the line (the operations applier).
 *
 * The builder's reorder and the operations applier both read this, so what a
 * drag shows is what the Save commits.
 */
export function exposureBoundaryBeforeMove<
  B extends QuestionnaireExposureBoundary | null | undefined,
>(
  questions: ReadonlyArray<{ stableKey?: string | null }>,
  boundary: B,
  stableKey: string,
): B {
  if (!boundary) return boundary;
  const at = questions.findIndex((question) => question.stableKey === stableKey);
  if (at < 0) return boundary;
  const reanchor = (
    mark: QuestionnaireExposureMark | null,
    neighbour: { stableKey?: string | null } | undefined,
  ) =>
    mark?.stableKey === stableKey
      ? { ...mark, stableKey: neighbour?.stableKey ?? null }
      : mark;
  const reveal = reanchor(boundary.reveal, questions[at - 1]);
  const sharedView = reanchor(boundary.sharedView, questions[at + 1]);
  return reveal === boundary.reveal && sharedView === boundary.sharedView
    ? boundary
    : ({ ...boundary, reveal, sharedView } as B);
}

/**
 * `postReveal(i)` from §2.3, as the derived output it now is.
 *
 * `undefined` rather than `false` on purpose, and absent on EVERY question when
 * the reveal is `-1`. That is PLAN's `B == 0` convention, kept because the two
 * withholders that arm on it (`derive_post_reveal`'s omit-everything branch and
 * the runner's `enable_reveal`) key on "is any flag set at all", so writing
 * `false` everywhere would arm a reveal that does not exist.
 */
export function derivedPostRevealAt(
  index: number,
  indexes: QuestionnaireExposureMarkIndexes,
): true | undefined {
  if (indexes.revealIndex < 0) {
    return undefined;
  }
  return index > indexes.revealIndex ? true : undefined;
}

export interface LaunchSnapshotPruneReport {
  droppedQuestionIds: Array<string>;
  droppedMetricIds: Array<string>;
  droppedQualitativeAnalysisIds: Array<string>;
  droppedSlideIds: Array<string>;
  droppedCompositeMetricIds: Array<string>;
  droppedUnnamedPaths: Array<string>;
}

export interface LaunchedQuestionnaireSnapshot {
  schemaVersion: "launch-snapshot.v1";
  registryVersion: "questionnaire-registry.v1";
  draft: QuestionnaireDraft;
  compiledSurveyJson: unknown;
  metricPlan: MetricPlan;
  slidePlan: SlidePlan;
  testedAssets: TestedAssetBlock;
  launchedBy: QuestionnaireLaunchActor;
  launchedAt: string;
  quickStudyQuote?: import('../canonical-quick-study-billing').QuickStudyQuoteApproval;
  /** Top-level copy. Already lived on persisted snapshots; declared so extra=forbid stays honest. */
  assumptions?: Array<Assumption>;
  /** Audit of questions dropped at persist because they were not compiled. */
  pruneReport?: LaunchSnapshotPruneReport;
}

export interface QuestionnaireLaunchActor {
  userToken: string;
  name?: string | null;
  email?: string | null;
}

export interface CompilerWarning {
  path: string;
  code: string;
  message: string;
}

export interface CompilerCoverageReport {
  researchQuestionIds: Array<string>;
  compiledQuestionIds: Array<string>;
  missingQuestionIds: Array<string>;
  surveyJsQuestionNames: Array<string>;
}

export interface CompileSurveyInput {
  draft: QuestionnaireDraft;
  testedAssets: TestedAssetBlock;
  options: {
    validateOnly: boolean;
  };
}

export interface CompileSurveyOutput {
  surveyJson: unknown;
  warnings: Array<CompilerWarning>;
  coverage: CompilerCoverageReport;
}

/**
 * A single selectable choice on a decision-card assistant message.
 * ``label`` is the button display text; ``value`` is auto-sent on selection.
 *
 * Named here rather than in the backend so both TypeScript sides can import
 * one shape: `apps/backend/.../chat/types/chat.types.ts` and
 * `apps/frontend/.../questionnaire/types.ts` each carried their own copy,
 * which made this contract a hand-maintained restatement on top of the
 * Pydantic mirror. Re-exported from both old locations, so no call site moves.
 */
export interface ChatMessageOption {
  label: string;
  value: string;
  /**
   * The option's typed identity (plan S3.3, L16; S4.1). The turn request
   * carries the chosen id as `optionId`, and it closes the card's
   * clarification record (`closedBy.optionId`), which is what a later chain
   * step reads; the English `value` is never parsed.
   */
  id?: string;
}

/**
 * Inline intake surface on an assistant message (composer-as-forms).
 * ``urls`` renders URL rows (with an upload affordance for LP parity);
 * ``upload`` renders a file picker. Collected artifacts flow through the
 * existing testedAssets lane; the transcript records a plain-text receipt,
 * so the planner pipeline never sees anything but text.
 *
 * Named here for the same reason as `ChatMessageOption`: the backend and the
 * frontend both declared it, and the two copies drifting is what 422s a study.
 */
export interface ChatMessageCollect {
  kind: "upload" | "urls";
  /** testedAssets asset type the collected artifacts become. */
  assetType: string;
  /**
   * On a card whose files become new items: the family's study limit (the
   * registry's `maxPerStudy`, `newItemsCardOf`). The builder's intake counts
   * every saved asset of `assetType` against it, so the user can add the
   * limit less what the study holds; Nest's write refuses past it. Absent on
   * an attach card, whose items set what it takes (`attachCapacityOf`).
   */
  maxAssets?: number;
  /**
   * Present on an attach card: the existing items still without a picture,
   * in catalog order, that its files attach to, one file each. A file becomes
   * the media of every asset of its item; no asset is added. Absent on a card
   * whose files are the items themselves.
   */
  attachTo?: Array<ChatMessageCollectAttachItem>;
  /**
   * A card the user may pass without adding anything (one click): the upload
   * card a model asked for a file the user has (`materialCardOf`). An attach
   * card always may. Absent on a card that collects the study's material.
   */
  optional?: true;
}

/**
 * One item an attach card offers (F19): a product once for all its price
 * versions, else one asset.
 */
export interface ChatMessageCollectAttachItem {
  /**
   * The item as the card names it: the product's name for its price versions,
   * else `productVersionLabel` (a name, and its price when it has one).
   */
  label: string;
  /** The assets its file becomes the media of, in catalog order. */
  assetKeys: Array<string>;
}

/** A catalog rule a chat message states: its code and structural facts. */
export interface QuestionnaireChatRefusal {
  code: string;
  params?: import("./operations").QuestionnaireRefusalParams;
  /**
   * The questions a stopped turn's refusal affects, by the number the user
   * sees on each ("Q3"), for the builder to word "Affects Q3, Q5." in the
   * chat's language.
   */
  questions?: Array<string>;
}

export interface QuestionnaireNotDoneBase {
  what: string;
  /**
   * `not_done`; `nearest`: the turn did the nearest supported version of it
   * (S4.11). A part the questionnaire already did is no entry: the written
   * reply states it (`alreadyDone`).
   */
  status: "not_done" | "nearest";
  where: import("./revision.generated").RevisionRequestPart["where"];
  /**
   * The capability the builder lacks that the part needs, for the builder to
   * word (S4.11).
   */
  capability?: import("./capabilities").CapabilityId;
  /**
   * The catalog rule whose refusal left the part undone, with the typed
   * facts its user line is filled from (L4a): code attaches it, and the
   * builder and the reply state the rule's line as the reason, never the
   * model's words.
   */
  refusal?: QuestionnaireChatRefusal;
  /**
   * A file the user has for the study's tested assets, and the assets it
   * shows (B2): the reply carries the upload card that asks for it
   * (`materialCardOf`).
   */
  material?: import("./revision.generated").MaterialRequest;
}

/**
 * Why a part is not done, typed (rule 4: required, with no bare "none"): a
 * capability the builder lacks, the rule whose refusal left it undone, the
 * asset panel that can do it, or the file the user handed over. A
 * part the builder cannot say a reason for is not a part it lists.
 */
export type QuestionnaireNotDoneReason =
  | { refusal: QuestionnaireChatRefusal }
  | { capability: import("./capabilities").CapabilityId }
  | { where: "asset_panel" }
  | { material: import("./revision.generated").MaterialRequest };

/**
 * A request part the turn did not do as asked, or did only in its nearest
 * supported version, with its typed reason. Every part but a `done` one: the
 * reply's applied record states what the turn did.
 */
export type QuestionnaireNotDonePart = QuestionnaireNotDoneBase & QuestionnaireNotDoneReason;

/** Whether a part carries a reason (every part the builder lists does). */
export function hasNotDoneReason(part: QuestionnaireNotDoneBase): boolean {
  return Boolean(part.refusal || part.capability || part.where === "asset_panel" || part.material);
}

export interface QuestionnaireChatNotDone {
  /** What the user acted in: the builder's line follows it. */
  origin: "chat" | "objective_panel";
  parts: Array<QuestionnaireNotDonePart>;
  /**
   * How the study shows its versions in the draft this turn committed
   * (StudySpec `resolved.exposure.mode`), which a nearest part's line follows:
   * what was done instead of an order differs in a shared view and in isolated
   * passes. Null when the draft records no design; absent on replies stored
   * before.
   */
  exposure?: import("./study-spec.generated").StudySpecExposureMode | null;
}

/**
 * A rejected chat turn's own line (F16): nothing the model writes, so no
 * reason or place leaks into it. Nest stores it; the builder shows it in the
 * interface's language (`builderCardText`), above the parts not done.
 */
export const QUESTIONNAIRE_NOTHING_CHANGED_REPLY = "Nothing was changed.";

export interface QuestionBaseEvidence {
  reachedPercentOfAssigned?: number | null;
  assigned: number;
  shown: number | null;
  answered: number;
  unknown: number;
  excluded: Record<string, number>;
  respondentIds: string[];
  /** Legacy only: an English base label written before N4. Never read. */
  cohortLabel?: string | null;
  lanes?: Record<string, QuestionBaseEvidence> | null;
}
export interface ResponseCoverage {
  assignmentBasis?: "assignment_manifest" | "observed_results";
  assigned: number;
  questions: Record<string, QuestionBaseEvidence>;
  dispositions: Record<string, number>;
  exits: Array<Record<string, unknown>>;
}


/** Mirror of `models/questionnaire/contracts.py::MetricScope`. */
export type MetricScope = "pooled" | "lane_merged" | "unattributed";

/**
 * Aliased to the generated projection's own `MetricResult.baseKind`
 * (`../reporting/canonical-analysis-output.generated.ts`, itself generated from
 * `models/questionnaire/contracts.py::MetricBaseKind`)
 * rather than hand-spelled, so the two cannot drift.
 */
export type MetricBaseKind = NonNullable<GeneratedMetricResult["baseKind"]>;

/**
 * Aliased to the generated projection's own `MetricResult.observationUnit`
 * (`../reporting/canonical-analysis-output.generated.ts`, itself generated from
 * `models/questionnaire/contracts.py::MetricObservationUnit`)
 * rather than hand-spelled, so the two cannot drift.
 */
export type MetricObservationUnit = NonNullable<GeneratedMetricResult["observationUnit"]>;

export interface CompositeMetricResult {
  compositeMetricId: string;
  winningAssetId?: string;
  scoresByAssetId: Record<string, number>;
  componentContributions: Record<string, Array<CompositeMetricComponent>>;
  tieAssetIds: Array<string>;
  /** Assets ordered best→worst by composite score (deterministic, asset-id tiebreak). Optional on read for back-compat. */
  rankedAssetIds?: Array<string>;
}

export interface AnalysisWarning {
  path: string;
  code: string;
  message: string;
}

/**
 * T8 — analyst-facing analysis flag. Sibling to `AnalysisWarning` (engineering /
 * provenance notes); a flag qualifies a number the analyst sees and renders as a
 * chip beside it. `detail` is a DETERMINISTIC template string (numbers from
 * `data`), never LLM free-text. `scope` + the optional ids pin the flag to a
 * study/composite/asset/dimension so an asset-scope chip can sit beside its
 * podium row. The report renders flags ONLY (never `.warnings`), which also
 * avoids double-counting the `composite_tie` warning. Keep in parity with
 * AnalysisFlag in apps/python-api/models/questionnaire/contracts.py.
 */
export type AnalysisFlagSeverity = "info" | "caution" | "warning";
export type AnalysisFlagScope = "study" | "composite" | "asset" | "dimension";

export interface AnalysisFlag {
  code: string;
  severity: AnalysisFlagSeverity;
  scope: AnalysisFlagScope;
  compositeMetricId?: string;
  assetId?: string;
  metricId?: string;
  title: string;
  detail: string;
  data: Record<string, number>;
}

export interface QuadrantPoint {
  assetId: string;
  label: string;
  x: number;
  y: number;
  imageUrl?: string;
  /** LLM-written per-concept copy. Absent falls back to the deterministic template. */
  description?: string;
}

export interface QuadrantAxisResult {
  xMetricId?: string;
  yMetricId?: string;
  xAxisLabel?: string;
  yAxisLabel?: string;
  points?: Array<QuadrantPoint>;
  rationale: string;
  excludedMetricReasons: Record<string, string>;
}

/**
 * The part an exec column plays in the summary. A summary is written as the
 * decision brief: `recommendation`, `finding` (why this direction) and
 * `test_next`, in that order; the deck heads each column with its role's
 * label. The other roles are read on stories stored before it.
 */
export type SummaryStoryRole =
  | "finding"
  | "implication"
  | "tension"
  | "action"
  | "situation"
  | "recommendation"
  | "test_next";

/** Typed direction for two same-category matrix row reads in a summary beat. */
export interface SummaryStoryComparison {
  leftSourceId: string;
  rightSourceId: string;
  direction: "left_higher" | "right_higher" | "equal";
}

export interface SummaryStoryEvidence {
  value: string;
  label: string;
  kicker: string;
  /**
   * The one source this figure is read from. Required here because both the
   * python contract and `slide-schemas/exec-summary.schema.json` require it;
   * `deck-ui/renderer-internal-types.ts` types it optional, which is the
   * renderer's own tolerance, not this contract's.
   */
  primarySourceId: string;
  sourceIds: Array<string>;
  name?: string | null;
  /**
   * Additive structured support for a row-category comparison. Free summary
   * prose is not parsed; the Python writer validates source compatibility and
   * derives this direction from the cited values.
   */
  comparisons?: Array<SummaryStoryComparison>;
}

export interface SummaryStoryBeat {
  role: SummaryStoryRole;
  /** A decision-brief column has none; a story stored before it carries the writer's column title. */
  title?: string | null;
  /** The column body: 1-3 complete sentences, one bullet each, rendered as written. */
  points?: Array<string> | null;
  /** Legacy one-paragraph body of a story stored before `points` existed. */
  narrative?: string | null;
  /** Optional numeric anchor; exact-copy validated when present. */
  evidence?: SummaryStoryEvidence | null;
}

export type SummaryKpiStatistic =
  | "share"
  | "respondent_share"
  | "respondent_count"
  | "share_delta"
  | "currency_median"
  | "price_curve_crossing";
export type SummaryKpiValueFormat = "percent" | "count" | "points" | "currency";
export type SummaryKpiObservationUnit = "respondent" | "answer" | "rating" | "pair" | "row_answer";

/**
 * One typed, provenance-checked read for the exec-summary KPI strip.
 * Additive alongside `SummaryStoryEvidence` (D10): narrative beats keep
 * consuming that string evidence unchanged. Lives only on `SummaryStory.kpis`.
 */
export interface SummaryKpi {
  value: number;
  valueFormat: SummaryKpiValueFormat;
  unit: string;
  statistic: SummaryKpiStatistic;
  observationUnit: SummaryKpiObservationUnit;
  scope: string;
  validAnswerBase: number;
  /** Unknown identities stay `null`/`undefined`; never backfilled from an observation count. */
  uniqueRespondents?: number | null;
  sentence: string;
  primarySourceId: string;
  sourceIds: Array<string>;
  /**
   * Declared display precision from `MetricDisplaySpec.decimals`. Display-only:
   * the stored `value` is never rounded. Currency tiles honour this; percent
   * and count keep their existing spelling. Absent on analyses stored before
   * the field.
   */
  decimals?: number | null;
  /**
   * True when the read's base is thin. Absent on analyses stored before the
   * field; the exec-summary kpi plot draws the low-base note only when true.
   */
  lowBase?: boolean | null;
}

export interface SummaryStory {
  /** Who authored the story (P1): the writer (unset), or the quick-mode or failed-writer placeholder. */
  titleSource?: 'writer' | 'quick_mode' | 'writer_failed' | 'findings_withheld' | null;
  /** The exec summary's eyebrow: the study's decision as the reader's question (a short label on older stories). */
  eyebrow?: string;
  headline: string;
  deckHeadline?: string | null;
  reportNarrative?: string | null;
  readinessVerdict?: string | null;
  subtitle?: string | null;
  /**
   * The cover slide's own supporting line, authored by the narrative layer
   * rather than composed by the cover payload builder. Optional: an analysis
   * stored before this field existed carries no value and renders no subtitle.
   */
  coverSubtitle?: string | null;
  beats: Array<SummaryStoryBeat>;
  /**
   * The exec-summary KPI strip: 0-3 typed reads, decoupled from the SCR
   * cards. Optional/null because `analysisOutput` is persisted; old studies
   * acquire it on `rerun_analysis`.
   */
  kpis?: Array<SummaryKpi> | null;
  /** Typed section dependencies. Absent on analyses stored before them. */
  dependencies?: ResolvedStoryDependencies | null;
}

/**
 * One run of a displayed verbatim: the runs' texts join to the verbatim, and
 * a run with a `gloss` is followed on screen by " [gloss]" (F5′). The reader
 * twin of the analysis output's `QuoteTextRun`, pinned to it through the
 * types that carry it.
 */
export interface QuoteTextRun {
  text: string;
  gloss?: string | null;
}

export interface SlideNarrativeQuoteCard {
  title?: string | null;
  stat?: string | null;
  quote: string;
  attribution?: string | null;
  quoteId?: string | null;
  sourceIds?: Array<string>;
  assetId?: string | null;
  /**
   * The speaker's respondent id, copied from the assigned quote (plots plan
   * S): the deck joins the profile by it, never by the display label. Absent
   * on narratives stored before it (legacy label join).
   */
  respondentId?: string | null;
  /** `quote` glossed where it refers to a tested item by its shown label (F5′). */
  quoteRuns?: Array<QuoteTextRun> | null;
}

/**
 * One typed read for a slide's own KPI tile.
 *
 * A SUBSET of `SummaryKpi`, not the same type: the exec-summary strip picks
 * between candidates and has to prove its denominator, so it carries
 * `observationUnit`, `scope`, `validAnswerBase`, `uniqueRespondents` and
 * `primarySourceId`. A slide tile reads the statistic its own slide already
 * shows, so it carries only the figure, its formatting and the writer's
 * sentence. Mirrors `packages/slide-schemas/presets/rating-likert.schema.json`
 * (`properties.kpi`).
 */
export interface SlideNarrativeKpi {
  value: number;
  valueFormat: SummaryKpiValueFormat;
  /** Percentage points render as `pp`, never `%`. */
  unit: string;
  sentence: string;
  statistic?: SummaryKpiStatistic | null;
  sourceIds?: Array<string>;
}

export interface SpotlightCopyPart {
  text: string;
  sourceKey: string;
  answerIds: Array<string>;
}

export interface SpotlightNarrative {
  bodyLeft: SpotlightCopyPart;
  bodyRightLead: SpotlightCopyPart;
  bodyRightTail: SpotlightCopyPart;
}

export interface SlideNarrative {
  slideType: CanonicalSlideType;
  eyebrow?: string;
  actionTitle: string;
  actionSubtitle?: string | null;
  /** Shared exhibit paragraph; actionSubtitle retains interpretation-rail copy. */
  exhibitSupportingText?: string | null;
  insightBullets?: Array<string> | null;
  quoteCard?: SlideNarrativeQuoteCard | null;
  /**
   * The slide's own KPI tile read. Optional: the python mirror is
   * `extra="forbid"` and `analysisOutput` is persisted, so a narrative stored
   * before this field existed must still validate on reload; old studies
   * acquire the tile on `rerun_analysis`.
   */
  kpi?: SlideNarrativeKpi | null;
  spotlight?: SpotlightNarrative | GeneratedAnalysis.SpotlightVisibleNarrative | null;
  sourceIds?: Array<string>;
  /**
   * Who authored this narrative's reader text: a narrative writer, code
   * copying an existing typed label, or a placeholder that must never reach a
   * ready report. No "template" value exists (P1). Optional: absent on
   * narratives stored before the field existed.
   */
  titleSource?: 'writer' | 'label' | 'quick_mode' | 'replay_stub' | null;
}

export interface RecommendationAction {
  stepNumber: number;
  icon: string;
  title: string;
  body: string;
  sourceIds: Array<string>;
  isHighlight?: boolean | null;
}

export interface RecommendationStory {
  /** Who authored the story (P1): the writer (unset), or the quick-mode or failed-writer placeholder. */
  titleSource?: 'writer' | 'quick_mode' | 'writer_failed' | 'findings_withheld' | null;
  /** Required on the python side, as on every other narrative contract. */
  eyebrow?: string;
  actionTitle: string;
  introParagraph: string;
  introBullets: Array<string>;
  /**
   * 1–3 action cards. Contiguous `stepNumber` values matching length:
   * `[1]`, `[1, 2]`, or `[1, 2, 3]`.
   */
  actions: Array<RecommendationAction>;
}

export interface QualSlideInsightBlock {
  label?: string | null;
  text: string;
  sourceIds?: Array<string>;
}

export interface QualThemeBlockRef {
  themeId: string;
  quoteId: string;
}

export interface QualEditorialFactReference {
  respondentId?: string;
  sourceKey: string;
  answerIds?: Array<string>;
}

export interface QualEditorialThemeAnalysis {
  themeId: string;
  scopeId?: string | null;
  title: string;
  text: string;
  quoteId?: string | null;
  sourceIds: Array<string>;
}

/** One per-creative featured person; scopeId is always `asset:<id>`, never `all`. */
export interface QualEditorialScopeFeatured {
  scopeId: string;
  quoteId: string;
  blurb: string;
  factReferences?: Array<QualEditorialFactReference>;
}

export interface QualSlideNarrative {
  slideType: QualitativeSlideType;
  /** Required on the python side, as on every other narrative contract. */
  eyebrow?: string;
  actionTitle: string;
  actionSubtitle?: string | null;
  insightBlocks?: Array<QualSlideInsightBlock>;
  featuredQuoteId: string;
  /** Additive writer-owned fields used only by the editorial layout. */
  editorialIntroduction?: string | null;
  editorialThemeAnalyses?: Array<QualEditorialThemeAnalysis>;
  featuredBlurb?: string | null;
  featuredFactReferences?: Array<QualEditorialFactReference>;
  /** Per-creative left-column people. Absent/empty on legacy narratives. */
  scopeFeatured?: Array<QualEditorialScopeFeatured>;
  themeBlockRefs: Array<QualThemeBlockRef>;
  sourceIds?: Array<string>;
  /**
   * Negative slides only: answers in any negative-typed theme over the coded
   * base, as the writer saw it. The slide always shows an objection's
   * verbatim and theme; a watch-out with no verbatim to show is not written.
   */
  objectionCount?: { answerCount: number; answerBase: number } | null;
}

export interface StoryPlanSlideContext {
  slideId: string;
  slideType: CanonicalSlideType;
  slideRole: string;
  analystFrame: string;
  implication: string;
  sourceIds: Array<string>;
  /** Typed section dependencies. Absent on analyses stored before them. */
  dependencies?: ResolvedStoryDependencies | null;
  /** The bare cut requestId this slide's chart is split by, in place; absent when the chart is plain. */
  chartSplitBy?: string | null;
  chartSegmentDefault?: ResolvedChartSegmentDefault | null;
}

/**
 * What one resolved story item was written from: the section
 * it fills, every question and metric it reads, its evidence, and the facts
 * and plan revisions. A targeted retry reuses the item only while both
 * revisions still match.
 */
export interface ResolvedStoryDependencies {
  sectionKind: "exhibit" | "section";
  sectionKey: string;
  questionIds?: Array<string>;
  metricIds?: Array<string>;
  compositeMetricIds?: Array<string>;
  evidenceIds?: Array<string>;
  factsRevision: string;
  planRevision: string;
}

export interface StudyStoryPlan {
  studyThesis: string;
  storyArc: Array<string>;
  slideContexts?: Record<string, StoryPlanSlideContext>;
  /** The plan revision. Absent on analyses stored before it. */
  planRevision?: string | null;
  /** The slides the accepted storyline carries, in order. Absent on plans stored before it. */
  acceptedSlideIds?: Array<string> | null;
  /** Whether the storyline was accepted or unavailable. Absent on plans stored before it. */
  storylineStatus?: "accepted" | "unavailable" | null;
  /** Slides whose brief cited evidence the slide does not draw, so it was removed (C16-#54a). */
  outOfBoundBriefSlideIds?: Array<string> | null;
}

/** How a KPI's number should be displayed. Absent → legacy `value` regex parse. */
export type FeaturedCardKpiValueType = "percent" | "number" | "score" | "delta";

export interface FeaturedCardKpi {
  /** Small eyebrow above the bold text ("Leads", "Top issue"). */
  label: string;
  /** Legacy display string ("Pringles, 85%") — always present, used as the parse fallback. */
  value: string;
  highlight?: boolean;
  /** The bold noun alone ("Pringles", "Lower fees"). */
  displayLabel?: string;
  /** The parsed number (85, 7.9, 24, -12). */
  numericValue?: number;
  valueType?: FeaturedCardKpiValueType;
  /** Unit suffix, e.g. "/10" for score, "%" for delta. */
  unit?: string;
}

export interface FeaturedCardBar {
  label: string;
  segments: Array<{ label: string; value: number }>;
  /** Optional creative thumbnail. Absent / undefined = title-only (no broken image). */
  imageUrl?: string;
  /**
   * Display text for the bar's number, when the segment value is NOT a percent.
   * Segment values double as bar geometry (0..100), so a scale mean or a signed
   * delta is normalized for width while printing its true value: a 3.9/5 mean is
   * 73% of the track but reads "3.9", and a +12 pt delta reads "+12 pts".
   * Absent / undefined = render the segment value as a percent.
   */
  valueLabel?: string;
}

export interface FeaturedCardLegendItem {
  label: string;
  color?: string;
}

export type FeaturedCardChartType = "horizontal-bars";

export interface FeaturedCard {
  questionId: string;
  title: string;
  /** Real question text, resolved server-side. Falls back to `title` for older persisted analyses. */
  questionLabel?: string;
  chartType: FeaturedCardChartType;
  kpis: Array<FeaturedCardKpi>;
  bars: Array<FeaturedCardBar>;
  legend: Array<FeaturedCardLegendItem>;
  footnote?: string;
}

/** Wave 6 featured selector: ids from the recipe candidate set, plus why. */
export interface FeaturedSelection {
  kpiIds: Array<string>;
  plotIds: Array<string>;
  predicates?: Record<string, string>;
  reason: string;
  source: "llm" | "fallback";
}

export type ScorecardDirection = "up" | "flat" | "down";

export type LiftScorecardAssetScore = GeneratedAnalysis.LiftScorecardAssetScore;

/** Decision 20: one view's movement (Top N share or NPS score) over the same pairs. */
export type LiftStatFigures = GeneratedAnalysis.LiftStatFigures;

/** The paired test a movement's change takes (P2), named in the study language. */
export type LiftStatTest = GeneratedAnalysis.LiftStatTest;

/** Decision 20: a movement's Top N / NPS views and its Average test, one machine payload. */
export type LiftMovementViews = GeneratedAnalysis.LiftMovementViews;

/** Decision 20: the lift card's view state (the view it opens on). */
export type LiftCardViews = GeneratedAnalysis.LiftCardViews;

export type ReportCardId =
  | "lift"
  | "diagnostics"
  | "podium"
  | "quadrant"
  | "kpi_rings"
  | "comparison"
  | "price_curve"
  | "quotes";

/**
 * A recipe corner name of a positioning map, as a `deck_labels.py` key (plots
 * plan O, owner decision 6). Kept equal to Python's `QuadrantCornerLabelKey`
 * (`models/questionnaire/contracts.py`) by `tests/apps/python-api/test_quadrant_plot.py`.
 */
export type QuadrantCornerLabelKey =
  | "quadrant_corner_claim_top_right"
  | "quadrant_corner_claim_top_left"
  | "quadrant_corner_claim_bottom_right"
  | "quadrant_corner_claim_bottom_left"
  | "quadrant_corner_pain_top_right"
  | "quadrant_corner_pain_top_left"
  | "quadrant_corner_pain_bottom_right"
  | "quadrant_corner_pain_bottom_left"
  | "quadrant_corner_brand_top_right"
  | "quadrant_corner_brand_top_left"
  | "quadrant_corner_brand_bottom_right"
  | "quadrant_corner_brand_bottom_left";

/** One subject scored by the grid: a brand, a competitor, an item. */
export interface ComparisonTableColumn {
  id: string;
  label: string;
  baseSize: number;
}

/**
 * One measure, scored across every surviving column.
 *
 * Values are NATIVE -- a 4.7 is a 4.7 out of 5, not an index. The argument for
 * this card over the funnel it replaced is that no cut is chosen on the
 * reader's behalf, so normalising here would give the number back the one
 * property it exists to avoid.
 */
export interface ComparisonTableRow {
  metricId: string;
  label: string;
  valuesByColumn: Record<string, number>;
}

/**
 * A grid as measures-down, subjects-across, in native units.
 *
 * `unitLabel` is required rather than optional because a table of bare numbers
 * is exactly the failure this replaces: every row is on ONE scale, and saying
 * which lets a reader check 4.7 against the 1-5 the question asked.
 */
export interface ComparisonTable {
  columns: Array<ComparisonTableColumn>;
  rows: Array<ComparisonTableRow>;
  unitLabel: string;
  decimals: number;
  /** Named when the grid scored more measures than the card will show. */
  droppedMeasures: Array<string>;
}

export type PriceRole = "too_cheap" | "cheap" | "expensive" | "too_expensive";

export const PRICE_ROLES = [
  "too_cheap",
  "cheap",
  "expensive",
  "too_expensive",
] as const satisfies ReadonlyArray<PriceRole>;

export interface PriceCurvePoint {
  price: number;
  /** Share of the base on this curve at this price, 0-100. */
  share: number;
}

export interface PriceCurveSeries {
  role: PriceRole;
  label: string;
  points: Array<PriceCurvePoint>;
}

/**
 * The prices a surface is allowed to PRINT, rounded by the producer.
 *
 * Separate from the crossings on purpose: geometry is exact and labels are
 * rounded. Markers and band edges sit on the interpolated crossings; every
 * piece of text reads these, so one rounding policy lives at the producer and
 * no surface invents a second one.
 */
export interface PriceCurveReported {
  low: number;
  high: number;
  /**
   * Absent on a thin base: a point estimate the base cannot carry disappears
   * from every surface at once rather than from whichever ones checked.
   */
  point?: number | null;
}

/** The price span the chart draws, so one extreme answer cannot starve it. */
export interface PriceCurveWindow {
  low: number;
  high: number;
}

/**
 * A reference price the curve is read against: today, what they pay now.
 *
 * The value is the published all-respondent median of the reference question,
 * with the base it was taken on, so the marker and the read it quotes cannot
 * state two different figures. It exists only when the reference question and
 * the curve name the same currency code.
 */
export interface PriceCurveReference {
  value: number;
  baseSize: number;
  questionId: string;
  /**
   * ONE SPELLING (D8b). `value` stays exact because the marker is positioned
   * from it; this is the precision every surface PRINTS it at, taken from the
   * reference question's own metric display spec. Three surfaces used to pick
   * their own rule and printed 257.84, 257.8 and 258 for one figure.
   *
   * Additive and optional: a curve stamped before this field falls back to
   * each surface's existing rule rather than being back-filled with a guess.
   */
  displayDecimals?: number | null;
}

/**
 * The Van Westendorp curves and the four prices read off them.
 *
 * Every crossing sits inside the observed price range or the whole curve is
 * withheld: this produces a number a business will charge, so it refuses far
 * more readily than it reports.
 */
export interface PriceCurve {
  series: Array<PriceCurveSeries>;
  baseSize: number;
  /**
   * ISO code the four willingness-to-pay questions were asked in, when they
   * agreed on one.
   *
   * Null means the questionnaire never captured a currency, and the card shows
   * the numbers unlabelled and says so. It is NOT a licence to guess: a "32"
   * that silently means dollars to the reader and pounds to the respondent is
   * worse than a 32 with no symbol.
   */
  currencyCode?: string | null;
  pointOfMarginalCheapness: number;
  optimalPricePoint: number;
  indifferencePricePoint: number;
  pointOfMarginalExpensiveness: number;
  /**
   * Everything below is additive and optional. A curve stored before these
   * existed loads, renders and rebuilds its deck exactly as it did; rerunning
   * analysis upgrades it. Nothing is back-filled for an old report.
   */
  reported?: PriceCurveReported | null;
  /**
   * Response rows; complete sets of four; complete but out of order. None of
   * them counts who was ASKED: exposure is not evidenced here. `baseSize`
   * above is the base actually used.
   */
  respondentCount?: number | null;
  completeCount?: number | null;
  inconsistentCount?: number | null;
  /** True where the point estimate is withheld. */
  isThinBase?: boolean | null;
  displayWindow?: PriceCurveWindow | null;
  reference?: PriceCurveReference | null;
}

/**
 * What this study's report is supposed to look like, frozen at build time.
 *
 * The report has never known what kind of study it is looking at: the draft
 * carried no recipe identity, so every card inferred its right to exist from
 * leftover artefacts. The RESOLVED shape is stamped rather than the recipe id,
 * so a study built on one recipe version renders identically after the next
 * one ships.
 */
export type ReportSourceRef = RecipeModuleReportSource | CustomTopicReportSource;

export type ReportProjectionBindingName =
  | "quadrantAxes"
  | "liftBaseline"
  | "comparison"
  | "priceCurve";

export type ReportProjectionRequirement =
  | ReportSourcesProjectionRequirement
  | ReportDeclaredBindingProjectionRequirement;

/** Immutable inputs for re-projecting the live fields after normal edits. */
export type ReportingProjectionSpecV1 = ReportingProjectionSpec;
/** The recipe's binding declarations by module and role, and what the
 * report projection found for each (DESIGN-REBINDING). */
export type {
  DeclaredComparisonTable,
  DeclaredLiftBaseline,
  DeclaredPriceCurve,
  DeclaredQuadrantAxes,
  DeclaredReportAxis,
  ReportBindingOutcomes,
  ReportingDeclaredBindings,
  ReportingProjectionSpecV2,
} from "./draft.generated";

/**
 * How one of the three fixed summary slots is drawn.
 *
 * A different axis from `ReportCardId`, which stays as it is: that names a card
 * the old summary drew, this names how a slot is drawn and is the key the
 * frontend registry looks a component up by. The two overlap in spelling
 * (`quadrant`, `comparison`, `price_curve`, `quotes`) and nothing converts one
 * into the other.
 *
 * Closed. A shape with no component is a slot that renders nothing and says
 * nothing, so a new read is added here, to the payload union and to the
 * registry together, or not at all.
 */
export type CardShape = ReportCardShape;

/**
 * What a podium ranks, carried BESIDE the shape rather than encoded into it.
 *
 * A compound `podium:chosen` would make the registry split a string before it
 * could find a component. The basis changes the podium's DATA -- a chosen
 * winner has an escape row and option shares, a scored one has neither -- never
 * which component draws it.
 */
export type PodiumBasis = "chosen" | "distribution" | "scored" | "pictures";

/**
 * What became of one declared option (D10).
 *
 * `absent` and `unjoined` are different facts about a study: the first was
 * never asked for, the second was asked for and could not be matched back to a
 * question. Aggregate recipe absence copy belongs to the slot, while only an
 * `unjoined` alternative carries its own fixed system reason.
 */
export type ReportOptionStatus = "planned" | "absent" | "unjoined";

/**
 * The KPI area: an ordered option list, same construct as the plot (D3).
 *
 * `options` + `chosenIndex` are canonical. `rows` and `secondary` stay as
 * deprecated aliases until Step 3 rewrites the resolver and Step 6 rewrites
 * the recipes: a plan that still sends `rows` / `secondary` type-checks.
 * If both new and old keys are sent, `options` wins.
 */
export interface LiftScorecardRow {
  measureId: string;
  /**
   * The metric this row was built from. `measureId` is the grid's measure slug;
   * `AnalysisFlag` is keyed on `metricId`, so the card needs this to attach a
   * base-size or pairing warning to the row it belongs to. Absent on rows built
   * by the fallback pairing road, which joins two metrics.
   */
  metricId?: string;
  /**
   * The post half's question and its baseline (pre) question: the join key a
   * question view uses to find its rows. Lift tags name a grid-level measure
   * token, never a row's `measureId`.
   */
  questionId?: string | null;
  baselineQuestionId?: string | null;
  label: string;
  unit: string; // e.g. rating_1_5, percent_0_100
  /**
   * The pooled movement. Absent on a lane-merged lift (lanes are never
   * pooled): the row then carries its movement per asset only, in `byAssetId`.
   */
  pre?: number | null;
  post?: number | null;
  delta?: number | null;
  direction?: ScorecardDirection | null;
  /**
   * Scale polarity, from the question's own anchors. `direction` is already
   * polarity-aware; this field is what lets the card SAY which way is good,
   * because a row reading 4/5 beside appeal 4/5 is a different finding when
   * lower is better.
   */
  polarity?: RatingPolarity;
  byAssetId?: Record<string, LiftScorecardAssetScore>;
  /** People with both answers: pooled, or the fewest any asset rests on. */
  pairs?: number | null;
  /** Some figure on the row rests on fewer pairs than the paired-test minimum. */
  lowBase?: boolean | null;
  /** The pooled movement's twins who could not rate before. */
  notApplicableBefore?: PairedLiftNotApplicableBefore | null;
  /**
   * The grid row this row's movement belongs to, when the lift was paired per
   * row on a grid with two or more rows: one card row per grid row, nothing
   * across them. Absent on a single-row grid and every other lift road.
   */
  gridRowKey?: string | null;
  /** The author's label for `gridRowKey`. */
  gridRowLabel?: string | null;
  /**
   * That grid row's part in the study (`vocab.lift.row_role`): the tested
   * subject or a comparison. Absent when no study fact names a row.
   */
  gridRowRole?: GeneratedAnalysis.LiftScorecardRow["gridRowRole"];
  /**
   * Decision 20: the pooled movement on the Top N share and on the NPS score
   * over the same pairs, and the Average's test (`LiftMovementViews`); per
   * asset on `byAssetId`. Absent where the halves carry no typed statistics
   * (the row then reads Average only).
   */
  views?: LiftMovementViews | null;
}

export interface LiftScorecard {
  rows: Array<LiftScorecardRow>;
  defaultAssetId?: string; // winner (largest delta; stable id tie-break)
  assetOptions?: Array<{ id: string; label: string }>;
  /**
   * The design behind the numbers, in the study's language, when the baseline
   * is the twin's own unexposed read from the shared pass (the within-twin
   * pairing engine). Left unset for the recipe-declared control baseline and
   * for the tag fallback: those pair different things, and a card that named a
   * design it did not run would be worse than a card that names none.
   */
  designNote?: string | null;
  /** Hover copy for the face `designNote`. Asset-neutral. */
  designNoteHover?: string | null;
  /**
   * The paired-test minimum the rows' `lowBase` was judged against, stamped by
   * Python so the card can say why it withholds a change. Absent on
   * scorecards with no pair counts and on those stored before it.
   */
  minPairs?: number | null;
  /**
   * Decision 20: the card's view state; `defaultView` is the view the app
   * opens on (Top N, NPS, else Average). Absent on cards stored before it:
   * Average only, no tabs.
   */
  views?: LiftCardViews | null;
}

export interface PostDiagnosticScorecardRow {
  metricId: string;
  label: string;
  unit: string;
  score?: number;
  /** See LiftScorecardRow.polarity. */
  polarity?: RatingPolarity;
  scoresByAssetId?: Record<string, number>;
}

export interface PostDiagnosticScorecard {
  rows: Array<PostDiagnosticScorecardRow>;
  assetOptions?: Array<{ id: string; label: string }>;
}

/* -------------------------------------------------------------------------- *
 * Summary cards: the three fixed slots, assembled next to the numbers (D1).
 *
 * Mirrors `apps/python-api/models/questionnaire/contracts.py` by name. Declared
 * in Step 2 of the summary-cards plan and populated in Step 3: the analysis
 * producer, the deck-ui views and the frontend registry are parallel jobs and
 * only stay in step if they build against names that stopped moving first.
 * -------------------------------------------------------------------------- */

/**
 * Which frame a card sits in. Three slots, one construct each (D3). The KPI
 * area is an ordered option list (`kpi_rows` or `scorecard`); there is no
 * fourth card and no `kpi_secondary`.
 */
export type SummarySlotId = "kpi" | "plot" | "quotes";

/**
 * What a reader is told about one card (D2). Four states, and a producer
 * returns one of them rather than nothing.
 *
 * - `available` -- drawn.
 * - `absent` -- the read was never planned.
 * - `unjoined` -- planned, and the question could not be matched to the data;
 *   the diagnostic reason records the join failure.
 * - `suppressed` -- the producer ran and could not draw (too few items, no
 *   base, a curve whose crossings fell outside the observed range).
 */
export type SummaryCardStatus =
  | "available"
  | "absent"
  | "unjoined"
  | "suppressed";

/**
 * How a card asks to be LAID OUT, once its status says it can be drawn (S3).
 * Not a second status and not a selection: both cards still exist, both keep
 * their payloads, and the disposition only says whether this one wants its own
 * frame.
 *
 * - `standalone` -- its own frame, the shape every card has always had.
 * - `combined_with_plot` -- this KPI card restates a figure the plot already
 *   draws, so the two read as one wider panel and the plot takes the combined
 *   width.
 *
 * Absent means standalone: a card stamped before this existed is not rewritten.
 */
export type SummaryCardPresentation = "standalone" | "combined_with_plot";

/**
 * Which cut a KPI row's figure is. Closed so a producer cannot invent a
 * statistic the resolver does not know how to read off `metricResults` (D1).
 */
export type KpiStatistic =
  | "favourable_share"
  /**
   * The top two authored points of a select whose options are coded as
   * numbers. No rating unit, so no policy band: not favorable share.
   */
  | "top_two_share"
  | "mean"
  /**
   * Ulwick opportunity (D19): importance + max(importance - satisfaction, 0).
   * Two measures in, one number out, so the row names both inputs in
   * `derivedFromMetricIds` rather than one in `metricId`.
   */
  | "opportunity"
  | "rank_index"
  | "modal_share"
  /**
   * Retired: kept so an analysis stamped while it was still emitted parses.
   * Nothing produces it any more.
   */
  | "top_option_share"
  | "median"
  | "count"
  | "delta"
  | "label";

/**
 * `favourable_share` is the persisted WIRE TOKEN of the favorable-share
 * statistic, whose identity (and `MetricKind` spelling) is `favorable_share`.
 * Stored rows keep the token; readers compare the identity, normalized on read
 * by `kpiStatisticIdentity` (mirror of `kpi_statistic_identity` in
 * `models/questionnaire/contracts.py`), never the two spellings.
 * TS has no question context here, so a stored coded-select row carrying
 * `favourable_share` normalizes to `favorable_share` in this identity even
 * where Python (`kpi_rows.py`) resolves the same row to `top_two_share`; do
 * not branch on `favorable_share` for select rows on this side.
 */
export type KpiStatisticIdentity = Exclude<KpiStatistic, "favourable_share"> | "favorable_share";

export function kpiStatisticIdentity(
  statistic: KpiStatistic | null | undefined,
): KpiStatisticIdentity | null {
  if (statistic == null) {
    return null;
  }
  return statistic === "favourable_share" ? "favorable_share" : statistic;
}

/**
 * One row of the KPI column: a number, and the sentence it supports.
 *
 * `value` plus `valueType` rather than a pre-formatted string, because the same
 * row is drawn as a ring at one size and a bold figure at another, and a
 * formatted string cannot be re-cut. The cuts are fixed per response kind (D20)
 * and chosen by the producer, never here.
 *
 * `value` and `statement` stay nullable this pass so Step 7 can flip consumers
 * without a breaking change. `moduleTitle` / `cutLabel` / `predicate` are
 * reserved for the one-sentence row (D11) and unused until Step 7 / Step 10.
 */
export interface KpiListRow {
  label: string;
  value?: number | null;
  /**
   * `percent` for a share (top-2-box, modal option, top multi-select option),
   * `mean` for a native scale mean, `currency` for a median spend, `number` for
   * a bare count, `delta` for a signed movement, and `label` for a row that has
   * a statement and no figure at all.
   */
  valueType: "percent" | "mean" | "number" | "currency" | "delta" | "label";
  /**
   * The native unit the figure is on: an ISO currency code, a scale label
   * ("out of 5"), or null for a percent.
   */
  unit?: string | null;
  /** One line a buyer reads on its own, next to the figure. */
  statement?: string | null;
  /**
   * The metric this row was cut from. The number is read off
   * `metricResults[metricId]`, never re-derived (D1). Optional ONLY for a row
   * that names `derivedFromMetricIds` instead: a figure computed from two or
   * more metrics has no single one to point at.
   */
  metricId?: string | null;
  /**
   * The metrics a derived figure was computed from, when no single `metricId`
   * owns it (the opportunity score is the one case today). A row must name one
   * or the other: provenance is never absent.
   */
  derivedFromMetricIds?: Array<string> | null;
  /** Which cut `value` is. The resolver reads this, not `valueType`. */
  statistic: KpiStatistic;
  /** RecipeModule.title, stamped for the labelled fallback (D11 / Step 7). */
  moduleTitle?: string | null;
  /**
   * The cut's own words, e.g. "4–5" from ratingStatistics.labels.favorable
   * (D11 / Step 7). Unused until then.
   */
  cutLabel?: string | null;
  /**
   * Wave 6 prose. The model writes these words, never the number (D11).
   * Unused until Step 10.
   */
  predicate?: string | null;
  /** Optional caveat shown in the tooltip frame (e.g. 10-point Ulwick scale). */
  note?: string | null;
  /**
   * The question this row's number was cut from, and that question's own
   * authored title.
   *
   * STAMPED, NOT RENDERED (Fernando 2026-09-12). The KPI column is several
   * unrelated reads stacked in one frame, so the question belongs to the ROW
   * and not to the card: a single heading over the list would name one read
   * and silently caption the rest. The card still stamps `sourceQuestions`
   * with every question its rows came from, and nothing on this surface prints
   * either yet -- an export, a run log or a later row-level line has the fact
   * when it needs it, which is the whole reason to carry it now.
   */
  questionId?: string | null;
  questionText?: string | null;
  /** D3. PLAN's reader name for this read; the row's context line. */
  reportLabel?: string | null;
  /**
   * Once-per-asset cuts of the same statistic. Set only on a lane-merged
   * read: `value` stays null so nothing can treat the parent as a pooled
   * figure. Keys are tested-asset ids; omitted keys are lanes with no cut.
   */
  byAssetId?: Record<string, number> | null;
  /**
   * D2. Every option label sharing the top value, in option order, when two
   * or more tie. The row states the tie; it never names one as the answer.
   */
  tiedCutLabels?: Array<string> | null;
  /**
   * D2. Summed share of the options flagged `exclusive` ("None of these",
   * "Cannot recall"), which never compete for the cut, and their labels.
   */
  exclusiveShare?: number | null;
  exclusiveLabels?: Array<string> | null;
  /** D2. People who answered this read, and people in the study. */
  validAnswerBase?: number | null;
  studyBase?: number | null;
  /**
   * D18. The grid row a focal grid row was cut from (its breakdown key), so
   * its base and plot read that row, never the whole grid. Absent on every
   * other row.
   */
  rowId?: string | null;
  /** D2. Who a routed read was asked of, from its typed visibility rule. */
  cohort?: KpiRowCohort | null;
}

/**
 * The group a routed read was asked of, in the questionnaire's own words: the
 * earlier question and the answer labels that route a respondent here.
 * Mirrors `KpiRowCohort` in `models/questionnaire/contracts.py`.
 */
export interface KpiRowCohort {
  questionId: string;
  questionText: string;
  answerLabels: Array<string>;
  /** D3. PLAN's reader name for the people the rule admits. */
  label?: string | null;
}

export interface KpiListPayload {
  shape: "kpi_rows";
  rows: Array<KpiListRow>;
  /**
   * Column labels for `KpiListRow.byAssetId`, when two or more tested assets
   * have a cut. Same `{id, label}` shape as a scorecard. Absent below two
   * assets, and absent when no row breaks down by asset.
   */
  assetOptions?: Array<ScorecardAssetOption> | null;
  /**
   * DIAGNOSTICS ONLY, never rendered. The sentence the empty KPI column would
   * have carried, when the declared rows produced nothing and the D8 fallback
   * question's headline share replaced the whole column. Mirrors
   * `FallbackPayload.replacedReason`.
   */
  replacedReason?: string | null;
  /**
   * The rows as a `kpi` plot payload (packages/slide-schemas/plots/
   * kpi.schema.json, layout row), stamped by Python (plots plan F). Read
   * through the kpi read adapter; absent on cards stored before it (legacy
   * `resolveMetricFigure` read).
   */
  plot?: Record<string, unknown> | null;
}

/**
 * The existing `LiftScorecard`, tagged with its shape.
 *
 * A wrapper rather than an alias: the payload union discriminates on `shape`,
 * and `LiftScorecard` is also carried at the top level of the analysis output
 * where no discriminator belongs. Wrapping keeps one definition of the rows.
 */
export interface LiftPayload {
  shape: "lift";
  scorecard: LiftScorecard;
  /** The fewest pairs any drawn figure rests on, printed by the card frame (plots plan F). */
  baseSize?: number | null;
}

/** One ranked row: what it is, what it scored, and how to draw it. */
export interface RankingItem {
  id: string;
  label: string;
  /** The study's one item letter for this id (D4), from `itemTags`. */
  tag?: string | null;
  imageUrl?: string | null;
  /**
   * What `imageUrl` IS, so a surface can draw it honestly: an uploaded image,
   * a landing-page screenshot, or a video's first keyframe (which gets a play
   * glyph, because a still lifted out of a video is not a picture someone
   * designed). Absent when there is no preview at all.
   */
  previewKind?: "image" | "snapshot" | "video" | null;
  /**
   * What respondents read of an imageless item (the compiled `assetContext`
   * item's `respondentCopy`): drawn in its tile in place of a picture. Absent
   * when the item has a picture or respondents read no copy.
   */
  respondentCopy?: string | null;
  value: number;
  /** The figure as the card should print it ("42%", "4.3 / 5", "18.6"). */
  valueLabel?: string | null;
  /**
   * The trailing "no clear preference" row of a chosen podium. Drawn muted and
   * never crowned. A `scored` podium has no escape row: nobody voted.
   */
  isEscape: boolean;
  /**
   * Dense rank (1, 1, 2) stamped by the producer on a podium; the card draws it
   * and never re-ranks. Absent on stored cards and on the other ranking shapes,
   * whose readers keep today's order-based rank.
   */
  rank?: number | null;
}

/** One selectable ranking: a funnel stage, an equity dimension (D14). */
export interface RankingSeries {
  id: string;
  label: string;
  items: Array<RankingItem>;
  /**
   * Per-series figure meaning and machine unit. The payload-level `scoreLabel`
   * / `unit` describe the DEFAULT series only, so a `dim_rank` selector printed
   * the first series' label against every other series' bars once the reader
   * switched dimensions. Absent on a series that states neither, which falls
   * back to the payload-level pair.
   */
  scoreLabel?: string | null;
  unit?: string | null;
  /**
   * This series' winner rows (every tied top item) and its tie, stamped by the
   * ranking producer (plots plan I) on `per_item_rank` / `dim_rank`. Absent on
   * stored cards, whose reader keeps today's crown.
   */
  topIds?: Array<string> | null;
  isTie?: boolean | null;
}

/** The bar scale a ranking card draws on (plots plan I), stamped by Python. */
export type RankingScale = GeneratedAnalysis.RankingScale;

/**
 * Ranked bars. One payload for the three shapes that rank items.
 *
 * `podium`, `per_item_rank` and `dim_rank` differ in what they rank and in
 * whether they offer a selector, never in how a row is drawn (D13): rank badge,
 * item tile, label, bar, value. Three payload types would be three copies of
 * one row contract.
 */
export interface RankingPayload {
  shape: "per_item_rank" | "podium" | "dim_rank";
  /** Only on a podium: what the ranking is OF. */
  basis?: PodiumBasis | null;
  /** The ranking shown when there is nothing to select between. */
  items: Array<RankingItem>;
  /**
   * Two or more selectable rankings (D14). Empty for a single-series read; the
   * selector renders only when this holds two or more.
   */
  series: Array<RankingSeries>;
  defaultSeriesId?: string | null;
  /**
   * What the figure means, from the draft's own composite definition
   * ("Opportunity", "Preferred"), never invented at render time.
   */
  scoreLabel?: string | null;
  /**
   * Machine unit so `formatAbsoluteScore` can read it (`percent_0_100`,
   * `rating_1_5`, …). Not a suffix symbol and not a prose label.
   */
  unit?: string | null;
  /**
   * Respondents behind the ranking, so the card's frame can print the base once
   * instead of each surface guessing it from the rows. Absent when the producer
   * has no honest single number for the whole payload.
   */
  baseSize?: number | null;
  /** No winner emphasis: the top two could not be separated. */
  isTie: boolean;
  /**
   * A crowned podium's chip, a podium label key naming the crown's strength or
   * its small base. Absent on a tie, a podium with no read, and other shapes.
   */
  leadChip?: 'plot_lead_clear' | 'plot_lead_leads' | 'plot_lead_slight' | 'plot_small_base' | null;
  /**
   * The winner rows Python crowned (every tied top item; more than one exactly
   * when `isTie`), for the default ranking. Absent on stored cards.
   */
  topIds?: Array<string> | null;
  /**
   * Decimals every figure prints with (podium, per_item_rank, dim_rank),
   * stamped with the rounded `value`s so the card never re-rounds. Absent on
   * stored cards.
   */
  decimals?: number | null;
  /**
   * What every figure is (the plot's `figure`): on a podium the pick share, a
   * named composite's index, or a weighted / opportunity score; on
   * `per_item_rank` / `dim_rank` a share, a mean, or a ranking question's
   * weighted score. Absent on stored cards.
   */
  figure?: 'pick_share' | 'composite_index' | 'weighted_score' | 'share' | 'mean' | null;
  /**
   * The bar scale (plots plan I; a podium's is its board's,
   * `podium_board.board_scale`). Absent on stored cards (the reader keeps
   * today's track).
   */
  scale?: RankingScale | null;
  /**
   * What the podium ranks: the study's tested assets (drawn as the podium board)
   * or text items such as pain points and claims (drawn as ranking bars). Absent
   * on stored cards and other shapes.
   */
  itemKind?: 'tested_asset' | 'text' | null;
}

/**
 * One plotted item on the summary's map.
 *
 * Not `QuadrantPoint`, which is the deck's own axis result and is keyed
 * `assetId`: the summary plots pain statements and claims as readily as assets,
 * and it carries the study's one item letter (D4) so the map's legend and the
 * podium's tiles agree.
 */
export interface SummaryQuadrantPoint {
  id: string;
  label: string;
  tag?: string | null;
  x: number;
  y: number;
  imageUrl?: string | null;
}

export interface QuadrantPayload {
  shape: "quadrant";
  points: Array<SummaryQuadrantPoint>;
  xLabel: string;
  yLabel: string;
  /**
   * Where the axes cross on the 0-100 plane, stamped by Python (plots plan O).
   * Null on cards stored before the stamp, which split at the plane's middle.
   */
  xMidpoint?: number | null;
  yMidpoint?: number | null;
  /**
   * (topRight, topLeft, bottomRight, bottomLeft) display strings from the
   * recipe (D6). Written by cards stored before plots plan O only.
   */
  cornerLabels?: [string, string, string, string] | null;
  /**
   * The recipe's four corner name keys, same order; the app resolves them in
   * the viewer's UI language (owner decision 5). Null: unnamed quadrants.
   */
  cornerLabelKeys?:
    | [
        QuadrantCornerLabelKey,
        QuadrantCornerLabelKey,
        QuadrantCornerLabelKey,
        QuadrantCornerLabelKey,
      ]
    | null;
  unit?: string | null;
  /**
   * The map as a `quadrant` plot payload (packages/slide-schemas/plots/
   * quadrant.schema.json), stamped by Python (plots plan O). Absent on cards
   * stored before it.
   */
  plot?: Record<string, unknown> | null;
}

/**
 * A measures-by-items grid: the comparison table, or the diagnostics rows.
 *
 * Two producers, one frame. `table` is the declared competitor grid in native
 * units; `scorecard` is the post-exposure diagnostic rows. Exactly one is set,
 * and both are the EXISTING contracts rather than a third row shape.
 */
export interface GridPayload {
  shape: "comparison";
  table?: ComparisonTable | null;
  scorecard?: PostDiagnosticScorecard | null;
  /** The smallest base behind the grid, printed by the card frame (plots plan F). */
  baseSize?: number | null;
  /**
   * The grid as a `heat-grid` plot payload (packages/slide-schemas/plots/
   * heat-grid.schema.json), stamped by Python (plots Q2 follow-up). Read
   * through the heat-grid comparison read adapter; absent on cards stored
   * before it (legacy `table` / `scorecard` read).
   */
  plot?: Record<string, unknown> | null;
}

export interface DistributionRow {
  id: string;
  label: string;
  /** 0-100. */
  share: number;
  count?: number | null;
}

/**
 * One closed read's answer shares.
 *
 * `unit` is a SUFFIX SYMBOL and nothing else, because the card prints it
 * straight after the figure: a prose unit name here renders "621-5 scale".
 * Every row is a share of the base by contract (`DistributionRow.share` is
 * 0-100), so the symbol is the percent sign; the prose the scale is called
 * goes in `unitLabel`, which the card draws once, away from the numbers.
 */
export interface DistributionPayload {
  shape: "distribution";
  rows: Array<DistributionRow>;
  /** A short symbol printed directly after each figure ("%", "/5", "$"). */
  unit?: string | null;
  /**
   * What the read is on, in words ("1-5 scale", "Share of respondents").
   * Never concatenated onto a figure.
   */
  unitLabel?: string | null;
  baseSize?: number | null;
  /**
   * The read as a `choice-bars` plot payload (packages/slide-schemas/plots/
   * choice-bars.schema.json), with its share-donut alternative when eligible,
   * stamped by Python (plots plan J). Read through the choice-bars read
   * adapter; absent on cards stored before it (legacy `rows` read).
   */
  plot?: Record<string, unknown> | null;
}

/** The existing `PriceCurve`, tagged with its shape. See `LiftPayload`. */
export interface PriceCurvePayload {
  shape: "price_curve";
  curve: PriceCurve;
}

/**
 * One verbatim with the speaker chrome the card actually renders.
 *
 * Richer than `RespondentQuote` (text and sentiment) because the quotes card
 * shows an avatar stack, a name and a role line, and a quote with no speaker
 * reads as copy someone wrote rather than something a respondent said. The
 * selection comes from the same helpers the deck uses (D6), so the page does
 * not regress when the deck is off.
 */
export interface SummaryQuote {
  text: string;
  respondentId?: string | null;
  speakerName?: string | null;
  initials?: string | null;
  /** The segment or persona line under the name. */
  role?: string | null;
  /** The qualitative theme this quote was selected as evidence for. */
  themeId?: string | null;
  themeLabel?: string | null;
  sentiment?: "positive" | "neutral" | "negative" | "mixed" | null;
  /**
   * The open-end this verbatim ANSWERS, and that question's authored title.
   *
   * Per quote, not per card: a study's codeframe covers every open-end it
   * fielded, so one card's verbatims can come from several different prompts,
   * and a single heading over the carousel would attribute all of them to
   * whichever one happened to be first. The theme label is not a substitute --
   * "Setup takes too long" names what the quote is about, never what the
   * respondent was asked.
   *
   * `questionText` is the title as analysis read it off the draft, present in
   * every context the card renders in (an export, the golden gallery, a page
   * whose survey has not loaded). `questionId` rides along so a consumer
   * holding the compiled survey can resolve the fielded wording instead.
   */
  questionId?: string | null;
  questionText?: string | null;
  /**
   * The version collection attributed this verbatim to. A plain-string
   * open-end carries the row's assigned version; a per-asset answer carries
   * the dict key. Present whenever the row had one, including a shared
   * question asked before exposure. The quotes card decides whether to show
   * it; this field does not.
   */
  assetId?: string | null;
  /** `text` glossed where it refers to a tested item by its shown label (F5′). */
  textRuns?: Array<QuoteTextRun> | null;
}

export interface QuotesPayload {
  shape: "quotes";
  quotes: Array<SummaryQuote>;
  /**
   * The quotes as a `quote-list` plot payload (packages/slide-schemas/plots/
   * quote-list.schema.json, layout carousel), stamped by Python (plots plan
   * S): the speaker joined by respondent id and the typed Other flag. Read
   * through the quote-list read adapter; absent on cards stored before it
   * (legacy `QuotesView` read).
   */
  plot?: Record<string, unknown> | null;
}

/**
 * D8: draw this one question through the Survey Data tab's own card.
 *
 * Only the question id and a diagnostic. The card the page reuses takes the
 * survey, the responses and the canonical map as props and builds everything
 * else itself -- carrying a second, analysis-side copy of that question here is
 * how the two would come to disagree.
 */
export interface FallbackPayload {
  shape: "fallback";
  questionId: string;
  /**
   * The sentence the empty frame WOULD have carried, had the fallback not
   * replaced it: a producer's refusal, a join failure, or the recipe's own slot
   * sentence. Diagnostics only, never rendered -- the page shows a question
   * rather than an apology (D8, Fernando 2026-09-08), and this is here so a run
   * log can still say which read was displaced.
   */
  replacedReason?: string | null;
}

/**
 * D6: one question drawn whole through QuestionCard. Refs only.
 *
 * The card takes the survey, the responses and the canonical map as props and
 * builds everything else itself -- carrying a second, analysis-side copy of
 * that question here is how the two would come to disagree. Same reason
 * `FallbackPayload` carries only an id.
 */
export interface QuestionPayload {
  shape: "question";
  questionId: string;
  /**
   * The pre/post counterpart when this question is one leg of a lift pair
   * (`lift:pre:` / `lift:post:`). Absent when there is no pair.
   */
  pairedQuestionId?: string | null;
  /**
   * DIAGNOSTICS ONLY, never rendered. The sentence a declared plot option
   * would have carried when this question card replaced it. Mirrors
   * `FallbackPayload.replacedReason`.
   */
  replacedReason?: string | null;
}

/** One scorecard row: an id. Every display property is read off the metric. */
export interface ScorecardRowRef {
  metricId: string;
  /**
   * D2. A rating grid over items with no per-asset columns is one row per
   * grid row: `rowId` is the row's breakdown key and `rowLabel` its authored
   * label. Absent otherwise, and on scorecards persisted before the field.
   */
  rowId?: string | null;
  rowLabel?: string | null;
}

/** One tested asset a scorecard may show as a column, when two or more ran. */
export interface ScorecardAssetOption {
  id: string;
  label: string;
}

/**
 * D4: the KPI area's other shape. N metric refs, rendered compact.
 *
 * The row is an id; labels, values and units are read off `metricResults`
 * (D1), never copied here. `assetOptions` is populated from the tested set
 * when two or more assets ran.
 */
export interface ScorecardPayload {
  shape: "scorecard";
  rows: Array<ScorecardRowRef>;
  assetOptions?: Array<ScorecardAssetOption> | null;
  /**
   * DIAGNOSTICS ONLY, never rendered. Set only when this scorecard REPLACED a
   * KPI column whose declared options produced nothing, and carrying the
   * sentence that column would have shown. Mirrors
   * `KpiListPayload.replacedReason` and `FallbackPayload.replacedReason`.
   */
  replacedReason?: string | null;
  /**
   * The rows as a `kpi` plot payload (packages/slide-schemas/plots/
   * kpi.schema.json), stamped by Python (plots plan F): figures, bases, order
   * and each head-to-head row's winner. Read through the kpi read adapter;
   * absent on scorecards stored before it (legacy `resolveMetricFigure` read).
   */
  plot?: Record<string, unknown> | null;
}

/**
 * Discriminated on `shape`, which is also the key the frontend registry looks a
 * component up by (D1) -- so a payload that parses is a payload something can
 * draw.
 *
 * `demand_curve` has no member: it remains a wire-readable reserved value, but
 * current plans reject it until its producer, payload and renderer ship as one
 * capability. A payload type with no output chain would be a false promise.
 */
export type SummaryCardPayload =
  | KpiListPayload
  | LiftPayload
  | RankingPayload
  | QuadrantPayload
  | GridPayload
  | DistributionPayload
  | PriceCurvePayload
  | QuotesPayload
  | FallbackPayload
  | QuestionPayload
  | ScorecardPayload;

type AssertReportCardShapeParity<T extends true> = T;
type _SummaryCardPayloadShapeParity = AssertReportCardShapeParity<
  Exclude<CardShape, "demand_curve"> extends SummaryCardPayload["shape"]
    ? SummaryCardPayload["shape"] extends Exclude<CardShape, "demand_curve">
      ? true
      : false
    : false
>;

/**
 * One question a summary card's figures answer.
 *
 * `questionText` is the question's own authored title, copied here on purpose.
 * The card is drawn in contexts that hold no compiled survey at all (the
 * golden gallery, an export, a page whose survey has not loaded yet), so a
 * card that could only NAME its question would print nothing in exactly the
 * places a reader has the least other context. `questionId` rides along so a
 * consumer that does hold the survey can resolve the fielded wording instead.
 */
export interface SummaryCardQuestion {
  questionId: string;
  questionText: string;
}

/**
 * One slot of the summary page, as analysis resolved it.
 *
 * The page draws these and chooses nothing: no priority list, no cap, no
 * top-up, no inference from data shape (D1, D3). A card with no payload is an
 * empty frame with generic public copy. `reason` retains the detailed cause for
 * diagnostics and must not be rendered to readers.
 */
export interface SummaryCard {
  slot: SummarySlotId;
  shape: CardShape;
  /**
   * Which of the slot's ranked options this is. Null for the KPI list, which is
   * one option, and for the D8 fallback, which is no option at all.
   */
  optionIndex?: number | null;
  status: SummaryCardStatus;
  /**
   * Diagnostic detail for every status except `available`. Never public copy.
   */
  reason?: string | null;
  payload?: SummaryCardPayload | null;
  /**
   * The question(s) this card's figures answer, in the order the producer read
   * them. The page prints them directly above the card body: bars captioned
   * "Share" with no question over them are a chart nobody can interpret
   * (Fernando 2026-09-12), and the question is the one piece of context no
   * label, unit or score name carries.
   *
   * Empty or absent means the producer could not name a read, and the page
   * then prints no heading rather than a guess. The `fallback` shape stamps
   * nothing by design: that card draws the question itself.
   */
  sourceQuestions?: Array<SummaryCardQuestion> | null;
  /**
   * How this card asks to be laid out (S3). Absent or null is `standalone`,
   * which is every card stamped before this field existed. The renderer reads
   * the disposition and allocates width; it never decides redundancy itself,
   * and a `combined_with_plot` card whose plot did not draw stays visible with
   * the plot's failure sentence beside it.
   */
  presentation?: SummaryCardPresentation | null;
  /**
   * The base the card's frame prints is under the low-base line
   * (`is_low_reader_base`), whatever the card's kind: the frame marks it.
   */
  lowBase?: true | null;
}

/**
 * C3.3 — one group a profiling cut splits the sample into, with its observed
 * base. An option nobody chose is still a level, reported with a count of zero:
 * dropping it would let a reader infer the option was never asked.
 */
export interface ProfilingCutLevel {
  value: string;
  label: string;
  respondentCount: number;
  /**
   * The study verdict computed on this group alone. Set only on a cut PLAN
   * declared a segmentation cut that is selectable, on a study with a declared
   * primary.
   */
  primaryRead?: StudyVerdict | null;
}

/**
 * C3.3 — one profiling question, offered to analysis as a named cut.
 *
 * Derived by code from the segmentation cut PLAN declared or the
 * `profile:<role>` tag the assembly stage stamped, so the available cuts are a
 * fact about the plan rather than a second place where
 * someone decides what may be crossed against what. It names the cut and
 * states each level's base; the one split analysis computes is the primary read
 * per level (`ProfilingCutLevel.primaryRead`), on a cut PLAN declared.
 */
/**
 * A saved audience segment a study was launched on. Identity is `id`; `label`
 * is display only (two segments may share one); `conditions` is the
 * definition frozen at launch. Owner: `SelectedSegment` in
 * apps/python-api/models/questionnaire/contracts.py.
 */
export interface SelectedSegment {
  id: string;
  label: string;
  conditions?: Array<Record<string, unknown>>;
}

export interface ProfilingCut {
  questionId: string;
  stableKey: string;
  /** The compiled element name, i.e. the key the answer is stored under. */
  surveyJsName: string;
  /**
   * The question's `profile:<role>`; absent for a declared cut that carries
   * none. `"eligibility"` only on a declared cut (a screener admitting several
   * answers).
   */
  role?: PlanProfilingRole | null;
  label: string;
  levels: Array<ProfilingCutLevel>;
  /** Respondents with a usable answer. The denominator for every level. */
  respondentCount: number;
  /**
   * Whether applying this cut would split anything. False when fewer than two
   * levels drew a respondent — a cut with one occupied group returns the whole
   * sample and presents it as a segment. Stated rather than re-derived by each
   * consumer, so they cannot disagree.
   */
  selectable: boolean;
}

/**
 * Which questions a monadic run answered once per tested asset, and over what.
 *
 * Mirror of `models/questionnaire/contracts.py::MonadicLaneScope`. DERIVED
 * OUTPUT of the engine's own `partition_monadic`, never an authored field:
 * scope depends on a survey-LEVEL flag, so a question's scope is not knowable
 * from the question alone.
 *
 * The browser reads this and splits answers by asset key. It must never
 * re-derive scope from `assetBinding.mode` or from an answer's shape — a dict
 * is a legal non-monadic shape for ranking and matrix.
 *
 * `laneQuestionNames` holds compiled SurveyJS element names (the keys inside
 * `survey_data`), so the fact also covers questions that have no
 * `MetricResult`. It is a set of questions with no display order and is sorted
 * server-side. `laneAssetIds` is NOT sorted: it keeps the tested-asset roster
 * order the deck lays its lanes out in, so lane 1 of a chart and
 * `laneAssetIds[0]` are the same asset.
 */
export interface MonadicLaneScope {
  laneQuestionNames: Array<string>;
  laneAssetIds: Array<string>;
}

/**
 * D2 study verdict: the declared primary question's read and per-item
 * ranking, built in analysis before any writer runs. No significance test.
 * Mirrors `StudyVerdict` in `models/questionnaire/contracts.py`.
 */
export type StudyVerdictState =
  | "read"
  | "no_declared_primary"
  | "primary_not_measured"
  | "primary_has_no_base"
  | "primary_not_numeric"
  | "primary_no_single_number";

export interface StudyVerdictRankEntry {
  itemId: string;
  label: string;
  value: number;
  /** 1-based; equal values share a rank, so a tie is stated as a tie. */
  rank: number;
  /**
   * People behind this item's value when it has its own base (an asset
   * ranking's lane leaf). Null: the verdict's `validAnswerBase` is its base.
   */
  base?: number | null;
}

/**
 * The acceptable price range, when it is the study's primary read.
 * Copied from the producer's published prices. `primaryRead` stays the
 * declared question's own input read.
 */
export interface StudyVerdictPriceRange {
  low: number;
  high: number;
  point?: number | null;
  currencyCode?: string | null;
  baseSize: number;
  roleQuestionIds: Array<string>;
}

export interface StudyVerdict {
  state: StudyVerdictState;
  primaryQuestionId?: string | null;
  /**
   * Where the primary came from: its one owner, the `plan:primary` tag.
   * `plan_attribution` is on outputs written while the attribution's mirror
   * was read; nothing writes it now.
   */
  primarySource?: "plan_attribution" | "plan_primary_tag" | null;
  primaryRead?: KpiListRow | null;
  rankingBasis?: "asset" | "ballot" | "grid_row" | null;
  /**
   * What each ranking value is; a rated read names its policy share. Null for
   * an asset ranking with no rating policy (the primary read's own cut) and
   * for a verdict stamped before the field existed.
   */
  rankingStatistic?:
    | "share"
    | "mean"
    | "opportunity"
    | "rank_index"
    | "favorable_share"
    | "yes_share"
    | "promoter_share"
    | null;
  ranking?: Array<StudyVerdictRankEntry>;
  /**
   * How many entries the podium may crown: `podium_crown_size` of the primary
   * (1 for a single pick, a ranking, a grid or a lane; the bound for a bounded
   * pick). Null when the primary does not force a choice, and on verdicts
   * stamped before the field.
   */
  crownSize?: number | null;
  /**
   * C15b: entry k against entry k+1, k = `crownSize`, classified by the
   * read-strength owner: the strength of the podium's crown, which is given
   * whenever entry k prints ahead. Null when no paired read exists, and on
   * verdicts stamped before the field.
   */
  leadRead?: GeneratedAnalysis.CutReadStrength | null;
  validAnswerBase?: number | null;
  /** The people the study's results describe (qualified past a screener, else everyone). */
  studyBase?: number | null;
  /**
   * The primary's read rests on a base too thin to lead (`too_thin_to_lead`):
   * it never leads the cover, headline, exec strip or Featured card.
   */
  tooThinToLead?: boolean;
  /**
   * When set, the range is the study's primary read and `primaryRead` is
   * the declared question's own input read.
   */
  primaryPriceRange?: StudyVerdictPriceRange | null;
}

/**
 * The stored canonical analysis and its metric results are the GENERATED
 * projection of the Pydantic owner (`models/questionnaire/contracts.py`), not a
 * hand copy. It is the schema's validation-mode
 * shape: an `Optional[...]` field reads `T | null` and a defaulted one reads
 * optional.
 */
export type {
  CanonicalAnalysisOutput,
  MetricResult,
  QualitativeQuote,
  QualitativeTheme,
  QualitativeThemePackage,
  QualitativeThemeVariantShare,
  RespondentQuote,
  Speaker,
} from "../reporting";

/**
 * The summary cards of a stored analysis as the discriminated `SummaryCard`
 * union the report reads. In the generated shape a payload's `shape` tag has a
 * default and so reads optional; the Python owner always serializes it, so a
 * stored card is this union. Pinned field for field to the generated card by
 * `canonical-analysis-output-parity.ts#SummaryCardMatchesGenerated`.
 */
export function summaryCardsOf(
  analysis: CanonicalAnalysisOutput | null | undefined,
): Array<SummaryCard> {
  return (analysis?.summaryCards ?? []) as unknown as Array<SummaryCard>;
}


/** A typed warning a slide-sequence rule raised; the deck records it unchanged. */
export interface SlideSequenceWarning {
  code:
    | 'price_curve_slides_condensed'
    | 'missing_required_metric'
    | 'lp_exhibit_dropped'
    | 'question_not_reached'
    | 'missing_required_qual'
    | 'qual_evidence_unavailable'
    | 'quadrant_axis_fallback'
    | 'planned_exhibit_unrendered'
    | 'duplicate_exhibit_omitted'
    | 'podium_skipped'
    | 'storyline_omitted'
    | 'composition_question_omitted'
    | 'study_findings_withheld';
  severity: 'info' | 'warning' | 'error';
  message: string;
  slideId?: string | null;
  questionId?: string | null;
  metricId?: string | null;
}

export interface ReportSlideSequence {
  items: Array<SlidePlanItem>;
  warnings?: Array<SlideSequenceWarning>;
}

export interface SampleDemographicSegment {
  value: string;
  count: number;
  percentage: number;
}

export interface SampleDemographicDimension {
  id: string;
  label: string;
  source: "mandatory" | "b2b" | "segmentation" | "filter";
  segments: Array<SampleDemographicSegment>;
}

/**
 * D2. One screener that qualified respondents: its question and the answer
 * labels that let a respondent continue. Mirrors `ScreenerCriterion` in Python.
 */
export interface ScreenerCriterion {
  questionId: string;
  questionText: string;
  qualifyingAnswerLabels: Array<string>;
}

/**
 * D2. How many assigned respondents qualified past the screener, counted from
 * the delivery record, and every screener criterion they met. Mirrors
 * `QualificationIncidence` in Python.
 */
export interface QualificationIncidence {
  qualified: number;
  assigned: number;
  screenerCriteria?: Array<ScreenerCriterion>;
}

export interface SampleDemographics {
  sampleSize: number;
  audienceType?: string | null;
  dimensions: Array<SampleDemographicDimension>;
  /** D2. The qualifying incidence; absent when nobody was screened out. */
  incidence?: QualificationIncidence | null;
}

export type CoverageLevel =
  | "covered"
  | "partial"
  | "out_of_domain"
  | "unavailable";

export interface QuestionCoverageResult {
  questionId: string;
  questionHash: string;
  level: CoverageLevel;
  rationale: string;
}

export interface CoverageAssessment {
  schemaVersion: "coverage-assessment.v1";
  coverageProfileHash: string;
  results: Array<QuestionCoverageResult>;
  computedAt: string;
}

/**
 * Clarifier code latched when the questionnaire builder's global topic gate is
 * asked. Recorded in `chatState.askedClarifierCodes` so the gate is asked at
 * most once per drafting session.
 */
export const TOPIC_GATE_CLARIFIER_CODE = "need_topic_gate";

/**
 * Sentinel `value` on the topic gate's "No" option. The chat UI intercepts this
 * and opens the audience picker instead of sending it to the planner, so it
 * must never reach a planner turn. Shared because the backend authors the
 * option and the frontend matches on it.
 */
export const TOPIC_GATE_CHANGE_AUDIENCE = "__change_audience__";

/**
 * The gate cards the builder words from their clarification record's code
 * (`questionnaire.opening.<code>.*`), like an opening-chain step: the topic
 * gate's closing question and buttons, and the landing-page pricing question.
 * Nest stores their English for the models; the chat words them in its own
 * language.
 */
export const LP_PRICING_GATE_CLARIFIER_CODE = "need_lp_gate_pricing";
export const GATE_CARD_CLARIFIER_CODES: ReadonlyArray<string> = [
  TOPIC_GATE_CLARIFIER_CODE,
  LP_PRICING_GATE_CLARIFIER_CODE,
];

/**
 * The question Nest asks when a tested item still lacks its concrete detail,
 * by what is missing: Nest stores the English (the models read it), and the
 * builder words the same line from `questionnaire.opening.asset_details.<id>`
 * in the chat's language.
 */
export const ASSET_DETAIL_QUESTIONS = {
  default:
    "I can draft this study, but I need one concrete asset detail first. What should each tested item say, show, or promise?",
  message_text_required:
    "I can draft the message test, but I need the actual message text first. What are the messages or claims you want respondents to evaluate?",
  ad_content_required:
    'I can draft the ad test, but I need the actual ad first. Paste the ad copy, describe the creative (e.g. "15s video of a dog using the app, CTA: Download now"), or attach it.',
  concept_description_required:
    "I can draft the concept test, but I need one concrete description or differentiator for each concept first. What should each concept be known for?",
  asset_name_required:
    "I can draft the comparison, but I need the names of the tested items first. What brands, products, concepts, ads, or packages should be included?",
} as const;

/**
 * Explore confirm-card sentinels. Paste and Build manually are intercepted
 * in the chat UI and must never reach a planner turn. Draft is prose on
 * purpose: it becomes `latestUserMessage` on the first-generation Build.
 */
export const EXPLORE_CONFIRM_PASTE_QUESTIONNAIRE = "__paste_questionnaire__";
export const EXPLORE_CONFIRM_BUILD_MANUALLY = "__build_manually__";
export const EXPLORE_CONFIRM_DRAFT_QUESTIONNAIRE =
  "The objectives are right, draft the questionnaire.";

export { openEndClassifierMetricId } from "./open-end-classifier";
export {
  compositeIsLive,
  deriveLiveReportCapabilities,
  isSurveyDataOnlyQuestion,
  podiumCrownSize,
  primaryRanksTestedItems,
  projectReportingPlan,
  quadrantAxisMethod,
  retypeKeepsReportSource,
  reportingCoherenceIssues,
} from "./reporting-coherence";
export {
  CURRENT_GENERATED_REPORT_CARD_SHAPES,
  RENDERABLE_REPORT_CARD_SHAPES,
  REPORT_CARD_SHAPES,
  REPORT_CARD_SHAPE_CAPABILITIES,
} from "./report-card-capabilities";
export type {
  ReportCardFieldPolicy,
  ReportCardPodiumBasis,
  ReportCardRequirement,
  ReportCardShape,
  ReportCardShapeCapability,
  ReportCardShapeStatus,
  ReportCardSlot,
  ReportCardSourceQuestionType,
} from "./report-card-capabilities";
export type {
  CompositeLiveness,
  LiveLandingPageExhibitsCapability,
  LivePodiumCapability,
  LiveQuadrantCapability,
  LiveReportCapabilities,
  LiveReportCapabilityReason,
  LiveReportSourceCapability,
  LiveScorecardCapability,
  QuadrantAxisMethod,
  QuadrantVerdictDeclaration,
} from "./reporting-coherence";

// The lift grid awareness roster, projected from the rows.
export {
  isLiftAwarenessGrid,
  liftAwarenessEntities,
  projectLiftAwarenessEntities,
} from "./lift-awareness";
export type {
  LiftAwarenessProjection,
  LiftAwarenessResidual,
} from "./lift-awareness";

export {
  humanizeRowValue,
  reconcileAwarenessEntities,
  refreshedAwarenessEntities,
} from "./awareness-entity-source";

// The HTTP questionnaire-operation contract (plan Stage 1, L5).
export * from "./operations";

// A carried question's options, derived from its source (C07-1).
export { carriedChoiceOptions, carriedSourceOptions, withCarriedChoices } from "./carried-choices";
export {
  isPinnableAnswer,
  pinnedLastOrder,
  selectOptionsOrder,
  pinRank,
  pinnedRealAnswerRefusals,
} from "./pinned-options";
export type { PinnedRealAnswerRefusal } from "./pinned-options";
export {
  brandCompetitors,
  brandRoster,
  competitorsAfterGridEdit,
  isRosterEntryOf,
  mintCompetitorId,
} from "./brand-roster";
export {
  COMPETITOR_LIMIT_EXCEEDED,
  competitorCapIssues,
} from "./competitor-cap";
export type {
  BrandCompetitor,
  BrandRole,
  BrandRosterEntry,
  RosterGridEdit,
} from "./brand-roster";
export { assetLink, collectedAssetContent, savesCaptureOfLink } from "./collected-asset-content";
export { findMentionedAssets } from "./mentioned-assets";
export { normalText } from "./normal-text";
export type { MentionableAsset } from "./mentioned-assets";
export {
  DEPENDENT_LOGIC_CODE,
  assetRemovalRefusals,
  projectAssetRemoval,
  removalListSurface,
  removedAssetOf,
} from "./asset-removal";
export type {
  AssetRemovalLeave,
  AssetRemovalRefusal,
  AssetRemovalRefusalCode,
} from "./asset-removal";
export type { CarriedChoiceOption } from "./carried-choices";

// What depends on a question, answer, row, measure or scale: the one detector
// the applier and the editor read, and the one transform that clears it (L3).
export {
  clearDependents,
  conditionLeaves,
  dependentsOf,
  dependentsOfDelete,
  hasDeclaredNotApplicable,
  leafAfterAnswersLeave,
  thresholdScale,
} from "./question-dependents";
export type {
  ConditionLeafRecord,
  QuestionDependent,
  QuestionDependentKind,
  QuestionnaireRemovals,
  RemovedMembers,
} from "./question-dependents";

// The one copy of a refusal the user can act on (Save and chat).
export * from "./refusal-copy";

// The one key rule (`config/questionnaire-key-rules.json`): every key the
// backend or the builder mints or checks.
export * from "./identity";

// Every rule the builder enforces, declared once (the rule catalog).
export * from "./rule-catalog";
export * from "./rule-impl";
export {
  CAPTURE_SHOWS,
  isCaptureShows,
  NOT_THE_PAGE_CAPTURE_CODES,
  pageCaptureLineCode,
  refusedCaptureLineCode,
} from "./page-capture";
export type { CaptureShows } from "./page-capture";
export * from "./recipe-module-question-type";
export * from "./opportunity-role-tags";

// The one upload policy for an asset's media (B23): the route and the builder.
export * from "./upload-policy";

// The one study spec (plan Stage 3): contract, resolver, reader and the two
// manifests it reads.
export {
  STUDY_SPEC_SCHEMA_VERSION,
  StudySpecResolutionError,
  declaredDeckLayout,
  declaredVerdict,
  goalRecipe,
  recipeDeclarations,
  resolveStudyBrand,
  resolveStudySpec,
  studySpecOf,
} from "./study-spec";
export type {
  RecipeCompositeDeclaration,
  RecipeCompositeDimensionDeclaration,
  RecipeDecisionOutcomeDeclaration,
  RecipeVersionDeclarations,
  StudySpecCatalogRow,
  ResolvedStudyBrand,
  StudySpecResolutionCode,
  StudySpecResolverInput,
  StudySpecStudyBrand,
} from "./study-spec";
export type * from "./study-spec.generated";
export {
  defaultEndSurveyDisposition,
  normalizeRetainedEndSurveyDisposition,
} from "./end-survey-disposition";
export {
  BINDING_READS,
  bindingReadOf,
  bindingReadTag,
  derivedBindingMode,
  studyBindingFor,
  studyBindingMode,
  studyReadsPerAsset,
  withStudyBinding,
  withZoneBinding,
} from "./study-binding";
export type { BindingRead, StudyBindingMode } from "./study-binding";
export {
  ASSET_FAMILY_FACTS,
  COPY_MATERIAL_FAMILIES,
  DESCRIPTION_REQUIRED_FAMILIES,
  EXPOSURE_BOUND_FAMILIES,
  FILES_PER_ASSET,
  FILE_CARRYING_FAMILIES,
  LINK_MATERIAL_FAMILIES,
  MEDIA_MATERIAL_FAMILIES,
  TEXT_MATERIAL_FAMILIES,
  UPLOAD_ENRICHMENT_FAMILIES,
  assetFamilyTitleNoun,
  assetContentSupplied,
  assetFileShown,
  assetFiles,
  isAssetFamily,
  isShownAssetFamily,
} from "./asset-families";
export type {
  AssetFamilyFacts,
  AssetFamilyMaterial,
  TextMaterialFamily,
} from "./asset-families";
export {
  POINT_BUTTON_RATING_UNIT_IDS,
  POINT_LABEL_RATING_UNIT_IDS,
  RATING_SCALES,
  RATING_SCALE_POINTS,
  SUPPORTED_RATING_SCALES_TEXT,
  SUPPORTED_RATING_UNIT_IDS,
  englishList,
  isPointButtonRatingUnit,
  isSupportedRatingUnitId,
} from "./rating-scales";
export type { RatingScaleFacts, SupportedRatingUnitId } from "./rating-scales";
export {
  AD_PLACEMENTS,
  ASSET_DESCRIPTION_SOURCES,
  ASSET_NAME_SOURCES,
  BILLING_PERIODS,
  CONTINUOUS_UNITS,
  DURATION_UNITS,
  MESSAGE_CLAIM_TYPES,
  NOT_APPLICABLE_VALUE,
  STUDY_BRAND_SOURCES,
  isWholeNumberUnit,
} from "./vocabularies";
export type {
  AdPlacement,
  AssetDescriptionSource,
  AssetNameSource,
  BillingPeriod,
  DurationUnit,
  MessageClaimType,
  StudyBrandSource,
} from "./vocabularies";
export {
  CANONICAL_SLIDE_TYPES,
  METRIC_KINDS,
  QUALITATIVE_SLIDE_TYPES,
  isQualitativeSlideType,
} from "./canonical-vocabularies";
export type { QualitativeSlideType } from "./canonical-vocabularies";
// Compile-time only: re-exported so every program that type-checks this barrel
// also checks the hand reader types still pinned to the generated projection.
export type {
  AnalysisReportingPlanDriftIsExact,
  PinnedHandTwinsMatchGenerated,
  PinnedTwinListsAlign,
  ReportingPlanMatchesGenerated,
  SummaryCardMatchesGenerated,
} from "./canonical-analysis-output-parity";
